import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from './api';
import RefundRules from './RefundRules';
import UnclaimedCod from './UnclaimedCod';
import { OrderNo } from './OrderNo';
import { fmtWh } from './time';
import { PhoneCards } from './PhoneCards';
import { titleEn } from './lineName';
import { useDeskSignIn, isBadgeRequired } from './returns/DeskSignIn';
import { DefectsTab } from './returns/Defects';
import { RegisteredTab } from './returns/Registered';
import { makeScanGuard } from './scanGuard';
import { scanSound } from './scanSound';
import { returnsText } from './returnsText';
import { isBadgeCode } from './pp2/IdentifyBox';

const R = `${API}/returns`;

// The returns desk (/returns/accept) lives in returns/Desk.tsx since 2026-10-28 — scan and count only; the WMS
// decides. The composer (ReturnComposer) stays CS's tool on the order panel.

/** Edit an open exchange from the Exchange orders tab: the order's lines come from the
 *  Accept lookup (by WMS id), then the same composer runs in edit mode. */
function ExchangeEditor({ r, onDone }: { r: any; onDone: () => void }) {
  const [order, setOrder] = useState<any | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch(`${R}/lookup/${encodeURIComponent(r.wms_order_id)}?by=id`).then(async res => {
      if (!res.ok) { setErr('Could not load the order lines'); return; }
      setOrder(await res.json());
    }).catch(() => setErr('Could not load the order lines'));
  }, [r.wms_order_id]);
  if (err) return <div className="text-red-400 text-sm">{err}</div>;
  if (!order) return <div className="text-gray-600 text-sm">Loading the order…</div>;
  const products = (order.lines || []).map((l: any) => ({ variation_id: l.variation_id, product_name: l.product_name, quantity: l.ordered_qty }));
  return (
    <ExchangeComposer orderId={r.wms_order_id} products={products} webshopId={order.webshop_id}
      existing={{ id: r.id, returned_items: r.returned_items, wanted_items: r.wanted_items, reason_text: r.reason_text,
                  send_mode: r.send_mode, no_return: r.no_return, dispatched_at: r.dispatched_at }}
      onCreated={onDone} onCancelEdit={onDone} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Returns page — tabbed shell (/returns/:tab). Phase 1 fills the Returns tab.
// ─────────────────────────────────────────────────────────────────────────────
// Where a return stands — one vocabulary for the Accept banner, the search and the
// order panel (backend returns.return_history).
export const STAGE_LABEL: Record<string, string> = {
  accepted: 'Accepted', in_return_order: 'In return order', polishing: 'Polishing',
  polished: 'Polished', replenished: 'Replenished', archived: 'Archived',
  // a registered exchange whose old item has not come back yet (order_after_returns, 2026-09-21)
  expected: 'Expected back',
  // a piece the customer was told to keep came back and is held off sale (2026-10-03)
  held: 'Held off sale', written_off: 'Written off',
};
export const STAGE_TONE: Record<string, string> = {
  accepted: 'bg-gray-700 text-gray-200', in_return_order: 'bg-blue-900/70 text-blue-200',
  polishing: 'bg-amber-800 text-amber-100', polished: 'bg-blue-800 text-blue-100',
  replenished: 'bg-green-800 text-green-100', archived: 'bg-green-900/60 text-green-300',
  expected: 'bg-purple-900/70 text-purple-200',
  held: 'bg-teal-900 text-teal-100', written_off: 'bg-gray-800 text-gray-400',
};
/** an acceptance's kind as a badge: exchange · unclaimed COD · kept (held off sale) */
const KIND_LABEL = (k: string) => k === 'unclaimed_cod' ? 'unclaimed COD' : k === 'kept' ? 'held off sale'
  : k === 'pending' ? 'return or unclaimed COD? — to decide' : k;
const KIND_TONE = (k: string) => k === 'exchange' ? 'bg-purple-800 text-purple-100' : k === 'kept' ? 'bg-teal-900 text-teal-100'
  : k === 'pending' ? 'bg-red-800 text-red-100' : 'bg-amber-800 text-amber-100';
const moneyOf = (v: number | null | undefined, c?: string | null) =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(2)} ${c || 'EUR'}`;

/** One acceptance as the search and the order panel show it: header line, items with the
 *  two timestamps, refund. `compact` is the order panel's narrow column. */
/** CS decides a desk parcel the WMS could not (kind 'pending', returns._cod_verdict): a return (refund with the fee
 *  rules) or an unclaimed COD parcel (a case, no refund). The worker never sees this — it is the virtual side. */
function PendingDecision({ a, onDone }: { a: any; onDone?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const decide = async (decision: 'return' | 'unclaimed_cod') => {
    const what = decision === 'return' ? 'a RETURN — the refund is raised with the fee rules' : 'an UNCLAIMED COD parcel — no refund, a case is opened';
    if (!window.confirm(`Order ${a.order_number}: this parcel is ${what}?`)) return;
    setBusy(true); setMsg('');
    const r = await authFetch(`${R}/pending/${a.id}/decide`, { method: 'POST', body: JSON.stringify({ decision }) });
    setBusy(false);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(`⚠ ${typeof d.detail === 'string' ? d.detail : 'Could not save'}`); return; }
    setMsg(d.already ? `Already decided: ${d.already}${d.decided_by ? ` (${d.decided_by})` : ''}`
      : decision === 'return' ? '✓ Decided: return' : `✓ Decided: unclaimed COD${d.case ? ` — case ${d.case}` : ''}`);
    onDone && onDone();
  };
  return (
    <div className="mt-2 px-2 py-1.5 rounded border border-red-800/70 bg-red-950/30 text-xs">
      <div className="text-red-200">The WMS could not tell what this parcel is{a.cod_verdict_why ? `: ${a.cod_verdict_why}` : ''}. Nothing is refunded and the piece stays off the shelf until you decide.</div>
      <div className="mt-1.5 flex gap-2">
        <button disabled={busy} onClick={() => decide('return')} className="px-2 py-1 rounded bg-green-800 hover:bg-green-700 text-white disabled:opacity-40">It is a return</button>
        <button disabled={busy} onClick={() => decide('unclaimed_cod')} className="px-2 py-1 rounded bg-amber-800 hover:bg-amber-700 text-white disabled:opacity-40">Unclaimed COD</button>
      </div>
      {msg && <div className="mt-1 text-gray-300">{msg}</div>}
    </div>
  );
}

export function ReturnAcceptanceCard({ a, compact, onChanged }: { a: any; compact?: boolean; onChanged?: () => void }) {
  return (
    <div className={`${compact ? '' : 'bg-[#1a1d27] border border-gray-800 rounded-lg p-3'} text-sm`}>
      <div className="flex items-center gap-2 flex-wrap">
        {!compact && <OrderNo value={a.order_number} className="font-semibold" />}
        {!compact && <span className="text-gray-400">{a.customer_name}</span>}
        {!compact && <span className="text-gray-600 text-xs">{a.store_name}</span>}
        {a.kind !== 'return' && <span className={`text-[10px] px-1.5 py-0.5 rounded ${KIND_TONE(a.kind)}`}>{KIND_LABEL(a.kind)}</span>}
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${STAGE_TONE[a.stage] || 'bg-gray-700 text-gray-200'}`}>{STAGE_LABEL[a.stage] || a.stage}</span>
        {a.return_order?.code && <span className="text-gray-400 text-xs">{a.return_order.code}</span>}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Accepted {a.accepted_at ? fmtWh(a.accepted_at, 'datetime') : '—'} by {a.accepted_by || '—'} · {a.pieces} pc{a.pieces === 1 ? '' : 's'}
        {a.return_order?.archived_at && <> · archived {fmtWh(a.return_order.archived_at, 'datetime')}</>}
      </div>
      <div className={`mt-2 space-y-1 ${compact ? '' : 'pl-2 border-l border-gray-800'}`}>
        {(a.items || []).map((i: any, k: number) => (
          <div key={k} className="text-xs">
            <div className="text-gray-300">{i.qty_returned}× {i.article_number || titleEn(i)}{i.article_number && i.product_name && <span className="text-gray-500"> · {titleEn(i)}</span>}
              {(i.wrong_item || i.ordered_variation_id) && (
                <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200"
                      title={`Sold as ${i.ordered_article || i.ordered_variation_id || '?'}; this garment is what left and what comes back. Stock follows the garment; the refund is the sold line's.`}>
                  wrong item shipped{i.ordered_article ? ` · sold as ${i.ordered_article}` : ''}
                </span>
              )}
            </div>
            <div className="text-gray-500 flex gap-3 flex-wrap">
              <span>polished {i.polished_at ? fmtWh(i.polished_at, 'datetime') : (i.qty_scanned ? 'yes' : '—')}</span>
              <span>replenished {i.replenished_at ? fmtWh(i.replenished_at, 'datetime') : (i.qty_received ? 'yes' : '—')}</span>
              {i.shared_with?.length > 0 && <span className="text-amber-500" title="The return-order line is merged with other orders; polish/replenish counts are the merged line's">shared with {i.shared_with.join(', ')}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs mt-2">
        {(a.refunds || []).length === 0 ? (
          <span className="text-gray-500">{a.refund_policy === 'resent' ? 'No refund — the goods were re-sent'
            : a.refund_policy === 'kept' ? `No refund — the customer was told to keep it${a.note ? ` (${a.note.replace(/^customer keeps it — /, '')})` : ''}${a.kind === 'kept' ? '; held off sale' : '; restocked'}`
            : a.kind === 'pending' ? 'No refund yet — to decide' : a.kind !== 'return' ? 'No refund for this part' : 'No refund row'}</span>
        ) : (a.refunds || []).map((r: any) => (
          <div key={r.id} className={r.processed ? 'text-green-400' : 'text-amber-300'}>
            Refund {moneyOf(r.net_refund, r.currency)} {r.auto && (r.auto.status === 'done' || r.auto.status === 'sent')
              ? `via Paytrail ${r.auto.refunded_at ? fmtWh(r.auto.refunded_at, 'datetime') : ''}${r.auto.status === 'sent' ? ' (e-mail refund)' : ''}`
              : r.auto && ['queued', 'retry'].includes(r.auto.status) ? `queued for Paytrail (${RQ_STATUS[r.auto.status]?.label || r.auto.status})`
              : r.processed ? `processed ${r.processed_at ? fmtWh(r.processed_at, 'datetime') : ''} by ${r.processed_by || '—'}` : 'open in Manual refunds'}
            {r.returned_value != null && r.returned_value !== r.net_refund && <span className="text-gray-500"> · goods {moneyOf(r.returned_value, r.currency)}</span>}
          </div>
        ))}
      </div>
      {a.kind === 'pending' && <PendingDecision a={a} onDone={onChanged} />}
    </div>
  );
}

function ReturnSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      setLoading(true);
      authFetch(`${R}/search?q=${encodeURIComponent(q.trim())}`).then(r => r.json())
        .then(d => setResults(d.results || [])).catch(() => setResults([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="mb-5">
      <input value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search a return: order number, customer, R-code or tracking number…"
        className="w-full px-4 py-2.5 rounded-lg bg-[#1a1d27] border border-gray-700 focus:outline-none focus:border-blue-500" />
      {q.trim().length >= 2 && (
        <div className="mt-3 space-y-2">
          {loading && results === null && <div className="text-gray-600 text-sm">Searching…</div>}
          {results && results.length === 0 && !loading && <div className="text-gray-600 text-sm py-4 text-center border border-dashed border-gray-800 rounded-lg">No accepted return matches "{q.trim()}".</div>}
          {(results || []).map((a: any) => <ReturnAcceptanceCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

const TABS: { key: string; label: string; auto?: boolean }[] = [
  { key: 'list', label: 'Returns' },
  { key: 'manual', label: 'Manual refunds' },
  { key: 'exchanges', label: 'Exchange orders' },
  // the defects box (28.10): pieces found faulty at polishing or held by their reason — a manager decides
  { key: 'defects', label: 'Defects' },
  // returns the customer announced (28.10, Phase D): the portal's — and customer service's — registrations
  { key: 'registered', label: 'Registered' },
  { key: 'unclaimed', label: 'Unclaimed COD' },
  // Fernando, 2026-09-17: the refunds the system pays itself, visible for checking
  { key: 'queued', label: 'Queued refunds', auto: true },
  { key: 'settings', label: 'Settings' },
];

// How far a return order is worked (Fernando, 2.11): "done / of" — grey nothing yet, amber part, green all.
// The backend counts (list_return_orders): scanned per line at most what came back, an over-scan apart;
// replenished out of the pieces meant for a shelf (not the defects box, not the held / undecided lines).
function WorkedCount({ done, of, note }: { done: number; of: number; note?: string }) {
  const tone = of > 0 && done >= of ? 'text-emerald-400' : done > 0 ? 'text-amber-300' : 'text-gray-500';
  return (
    <span className={tone} title={note || undefined}>
      {done} / {of}{note && <span className="ml-0.5 text-gray-500">*</span>}
    </span>
  );
}

function scannedOf(o: any) {
  const over = Number(o.over_scanned || 0);
  return { done: Number(o.scanned_count || 0), of: Number(o.piece_count || 0),
           note: over > 0 ? `${over} more scanned than came back` : undefined };
}

function replenishedOf(o: any) {
  const notes: string[] = [];
  if (Number(o.defect_count || 0) > 0) notes.push(`${o.defect_count} in the defects box or written off`);
  if (Number(o.held_count || 0) > 0) notes.push(`${o.held_count} held off sale or undecided — a manager settles it`);
  return { done: Number(o.replenished_count || 0), of: Number(o.shelf_count || 0),
           note: notes.length ? `Not for a shelf: ${notes.join('; ')}` : undefined };
}

export function ReturnsPage() {
  const { tab = 'list' } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (tab === 'list') authFetch(`${R}/orders?status=${showArchived ? 'archived' : 'active'}`).then(r => r.json()).then(d => setOrders(d.return_orders || []));
  }, [tab, showArchived]);

  return (
    <div className="p-6 text-gray-200 max-w-6xl mx-auto">
      <h1 className="text-xl font-semibold mb-4">Returns</h1>
      <div className="flex gap-1 border-b border-gray-800 mb-5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => navigate(`/returns/${t.key}`)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}>
            {t.label}
            {t.auto && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/70 text-emerald-300 align-middle"
                title="Queued refunds by the system — just for checking">AUTO</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <>
          <ReturnSearch />
          <div className="flex justify-end mb-2">
            <button onClick={() => setShowArchived(s => !s)} className="text-xs text-gray-400 hover:text-white">
              {showArchived ? '← Active return orders' : 'View archived →'}
            </button>
          </div>
          {orders.length === 0 ? (
            <div className="text-gray-600 text-sm py-8 text-center border border-dashed border-gray-800 rounded-lg">{showArchived ? 'No archived return orders.' : 'No active return orders. Create one from Accept Returns.'}</div>
          ) : (
            <PhoneCards rows={orders} keyOf={o => o.id}
              card={o => (
                <div className="flex items-center justify-between gap-3" onClick={() => navigate(`/returns/order/${o.id}`)}>
                  <div>
                    <div className="text-white font-semibold">{o.code}{showArchived && <span className="ml-2 text-xs text-gray-600">archived</span>}</div>
                    <div className="text-xs text-gray-400">{o.created_at ? fmtWh(o.created_at, 'datetime') : '—'} · {o.created_by}</div>
                    <div className="text-xs text-gray-400">{o.piece_count} pieces · scanned <WorkedCount {...scannedOf(o)} /> · replenished <WorkedCount {...replenishedOf(o)} /></div>
                  </div>
                  <span className="px-3 py-1.5 rounded bg-gray-800 text-xs">Open</span>
                </div>
              )}>
            <table className="w-full text-sm bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
              <thead className="text-gray-500 text-left border-b border-gray-800">
                <tr>
                  <th className="py-2 px-3">Code</th>
                  <th className="py-2 px-3">Created</th>
                  <th className="py-2 px-3">By</th>
                  <th className="py-2 px-3 text-center">Pieces</th>
                  <th className="py-2 px-3 text-center">Scanned</th>
                  <th className="py-2 px-3 text-center">Replenished</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 px-3 font-semibold">{o.code}{showArchived && <span className="ml-2 text-xs text-gray-600">archived</span>}</td>
                    <td className="py-2 px-3 text-gray-400">{o.created_at ? fmtWh(o.created_at, 'datetime') : '—'}</td>
                    <td className="py-2 px-3 text-gray-500">{o.created_by}</td>
                    <td className="py-2 px-3 text-center">{o.piece_count}</td>
                    <td className="py-2 px-3 text-center"><WorkedCount {...scannedOf(o)} /></td>
                    <td className="py-2 px-3 text-center"><WorkedCount {...replenishedOf(o)} /></td>
                    <td className="py-2 px-3 text-right">
                      <button onClick={() => navigate(`/returns/order/${o.id}`)} className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs">Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </PhoneCards>
          )}
        </>
      )}

      {tab === 'manual' && <ManualRefundsTab />}
      {tab === 'exchanges' && <ExchangeOrdersTab />}
      {tab === 'defects' && <DefectsTab />}
      {tab === 'registered' && <RegisteredTab />}
      {tab === 'unclaimed' && <UnclaimedCod />}
      {tab === 'queued' && <QueuedRefundsTab />}
      {tab === 'settings' && <ReturnsSettings />}
    </div>
  );
}

// ── money at the price paid ───────────────────────────────────────────────────
// Fernando 2026-09-09: no separate discount row — the discount is tied to the
// product row it came off. So one cell: the paid price, and under it in grey the
// list price and the code with what it took off this piece. Nothing extra is
// drawn for a line that carried no discount, or for a legacy row that predates
// the paid/list split (those show what they stored: list = paid).
const fmtMoney = (n: number | null | undefined, cur?: string) =>
  n === null || n === undefined ? '—'
  : (!cur || cur === 'EUR') ? `€${Number(n).toFixed(2)}` : `${Number(n).toFixed(2)} ${cur}`;

function PaidCell({ unit_paid, gross_unit, discount_unit, code, currency }:
  { unit_paid?: number | null; gross_unit?: number | null; discount_unit?: number | null;
    code?: string | null; currency?: string }) {
  const off = Number(discount_unit || 0);
  const discounted = off > 0.001 && gross_unit !== null && gross_unit !== undefined;
  return (
    <div className="tabular-nums">
      <div className={discounted ? 'text-white' : 'text-gray-400'}>{fmtMoney(unit_paid, currency)}</div>
      {discounted && (
        <div className="text-gray-500 text-xs" title="List price on the order, and what the discount code took off this piece">
          list {fmtMoney(gross_unit, currency)} · {code || 'discount'} −{fmtMoney(off, currency)}
        </div>
      )}
    </div>
  );
}

// ── Manual refunds tab — to-do list, collapsible rows ─────────────────────────
function ManualRefundsTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [archived, setArchived] = useState(false);
  const [errs, setErrs] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    authFetch(`${R}/manual-refunds?processed=${archived}`).then(r => r.json()).then(d => setRows(d.manual_refunds || []));
  }, [archived]);
  useEffect(() => { load(); }, [load]);
  const toggle = (id: number) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // a refused Process (403, already processed, backend error) used to vanish
  // silently — the row simply stayed. Say what happened, on the row.
  const process = async (id: number, force?: { force: true; note: string }) => {
    setErrs(e => ({ ...e, [id]: '' }));
    const res = await authFetch(`${R}/manual-refunds/${id}/process`, { method: 'POST', body: JSON.stringify(force || {}) });
    if (res.ok) { load(); return; }
    const d = await res.json().catch(() => ({}));
    const why = (typeof d.detail === 'string' ? d.detail : d.detail?.message) || `Could not process (HTTP ${res.status})`;
    // above what the purchase can still give back (28.10): a manager may process it anyway, with a note on the order
    if (!force && d.detail?.code === 'over_ceiling' && ['manager', 'admin'].includes(localStorage.getItem('wms_role') || '')) {
      const note = window.prompt(`${why}\n\nProcess it anyway? Say why (required):`);
      if (note && note.trim()) { process(id, { force: true, note: note.trim() }); return; }
    }
    setErrs(e => ({ ...e, [id]: why }));
  };
  // Each refund is in ITS order's currency — Motley Denim rows are NOK/SEK/DKK,
  // and printing € over them overstated or understated every one.
  const money = (n: number, cur?: string) =>
    (!cur || cur === 'EUR') ? `€${n.toFixed(2)}` : `${n.toFixed(2)} ${cur}`;
  const dateOf = (s?: string) => s ? fmtWh(s, 'date') : '';

  const tabBtn = (val: boolean, label: string) => (
    <button onClick={() => { setArchived(val); setOpen(new Set()); }}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
        archived === val ? 'bg-blue-600 border-blue-500 text-white'
                         : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {tabBtn(false, 'To process')}
        {tabBtn(true, 'Archived')}
      </div>
      <p className="text-gray-500 text-sm">{archived
        ? 'Refunds already processed in the shop admin. The amount shown is what was refunded.'
        : <>MCF can't auto-refund — refund the <span className="text-gray-300">suggested amount</span> in the shop admin, then mark them processed. Aim: empty by end of day.</>}</p>
      {rows.length === 0 ? (
        <div className="text-gray-600 text-sm py-10 text-center border border-dashed border-gray-800 rounded-lg">
          {archived ? 'No archived refunds yet.' : 'No refunds to process. 🎉'}
        </div>
      ) : rows.map(r => {
        const deducted = (r.returned_value ?? r.amount_total) - r.amount_total;
        return (
        <div key={r.id} className="bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center gap-y-1 px-4 py-3 cursor-pointer hover:bg-gray-800/30" onClick={() => toggle(r.id)}>
            <span className="text-gray-500 w-5">{open.has(r.id) ? '▾' : '▸'}</span>
            <OrderNo value={r.order_number} prefix="Order " className="font-semibold w-auto mr-3 md:mr-0 md:w-32" />
            {r.kind === 'cancellation' && <span className="mr-2 text-[10px] px-1.5 py-0.5 rounded bg-red-900/60 text-red-200" title="Order cancelled before dispatch — the whole payment is refunded, no return rules">cancellation</span>}
            {r.kind === 'adjustment' && <span className="mr-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-900/60 text-sky-200" title="A refund override from the order page — a manager refunded part of what was kept (goodwill / not the customer's fault)">override</span>}
            <span className="text-gray-400 flex-1">{r.store_name}
              {/* the discount code that priced these lines — visible without opening the row */}
              {r.discount_code && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-900/50 text-purple-200 text-xs font-mono"
                      title="Bought with a discount code — the refund is the price paid, not the list price">
                  {r.discount_code}
                </span>
              )}
            </span>
            {r.auto && ['queued', 'retry', 'needs_person'].includes(r.auto.status) && (
              <span className={`mr-3 text-[10px] px-1.5 py-0.5 rounded ${r.auto.status === 'needs_person' ? 'bg-red-900/70 text-red-200' : 'bg-emerald-900/70 text-emerald-300'}`}
                title={r.auto.status === 'needs_person'
                  ? `The automatic refund stopped: ${r.auto.last_error || '?'} — Process here refunds it by hand`
                  : `The system pays this one through Paytrail (${r.auto.status}${r.auto.next_attempt_at ? ', next try ' + fmtWh(r.auto.next_attempt_at, 'datetime') : ''}). Process here only if you refund it by hand.`}>
                AUTO · {RQ_STATUS[r.auto.status]?.label || r.auto.status}
              </span>
            )}
            {r.flags?.length > 0 && <span className="mr-3 text-amber-400 text-xs" title={r.flags.join('\n')}>⚠ review</span>}
            <div className="text-right mr-4">
              <span className="font-semibold">{money(r.amount_total, r.currency)}</span>
              {deducted > 0.001 && <span className="text-gray-500 text-xs ml-2 line-through">{money(r.returned_value, r.currency)}</span>}
            </div>
            {archived
              ? <span className="text-xs text-gray-500 w-40 text-right">✓ {r.processed_by || '—'}{r.processed_at ? ` · ${dateOf(r.processed_at)}` : ''}</span>
              : <button onClick={e => { e.stopPropagation(); process(r.id); }} className="px-3 py-1 rounded bg-green-600 hover:bg-green-500 text-xs font-medium">Process</button>}
          </div>
          {errs[r.id] && <div className="px-4 pb-2 text-xs text-red-400">⚠ {errs[r.id]}</div>}
          {open.has(r.id) && (
            <div className="border-t border-gray-800">
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-left">
                  <tr><th className="py-1.5 px-4">Item returned</th><th className="py-1.5 px-4 text-center">Pieces</th><th className="py-1.5 px-4 text-right">Paid per piece</th><th className="py-1.5 px-4 text-right">Total</th></tr>
                </thead>
                <tbody>
                  {r.lines.map((l: any, i: number) => (
                    <tr key={i} className="border-t border-gray-800/50">
                      <td className="py-1.5 px-4 text-gray-300">{titleEn(l)}</td>
                      <td className="py-1.5 px-4 text-center">{l.qty}</td>
                      <td className="py-1.5 px-4 text-right">
                        <PaidCell unit_paid={l.unit_paid} gross_unit={l.gross_unit} discount_unit={l.discount_unit}
                                  code={l.discount_code} currency={r.currency} />
                      </td>
                      <td className="py-1.5 px-4 text-right">{money(l.line_total, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* refund calculation breakdown */}
              <div className="border-t border-gray-800 bg-[#13151d] px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Refund calculation</div>
                <table className="w-full text-sm">
                  <tbody>
                    {(r.breakdown || []).map((b: any, i: number) => (
                      <tr key={i} className={b.total ? 'border-t border-gray-700' : ''}>
                        <td className={`py-1 pr-4 ${b.total ? 'font-semibold text-white' : 'text-gray-300'}`}>{b.label}</td>
                        <td className="py-1 px-4 text-gray-500 text-xs">{b.note}</td>
                        <td className={`py-1 pl-4 text-right tabular-nums ${b.total ? 'font-semibold text-green-400' : b.amount < 0 ? 'text-red-400' : 'text-gray-300'}`}>
                          {b.amount < 0 ? `−${money(Math.abs(b.amount), r.currency)}` : money(b.amount, r.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {r.flags?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {r.flags.map((f: string, i: number) => (
                      <div key={i} className="text-amber-400 text-xs">⚠ {f}</div>
                    ))}
                  </div>
                )}
                {r.shipping_method && <div className="text-gray-600 text-xs mt-2">Original shipping method: {r.shipping_method}</div>}
              </div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

// ── Queued refunds · AUTO ─────────────────────────────────────────────────────
// The refund queue (backend payments/refund_worker.py): every auto-refundable acceptance
// becomes a job the system pays through Paytrail, retrying when the account holds no
// funds. Open = still to pay; History = paid / sent / taken over by a person.
const RQ_STATUS: Record<string, { label: string; cls: string }> = {
  queued:       { label: 'Queued',          cls: 'bg-gray-700 text-gray-200' },
  sending:      { label: 'Sending…',        cls: 'bg-blue-900/70 text-blue-200' },
  retry:        { label: 'Retrying',        cls: 'bg-amber-800 text-amber-100' },
  needs_person: { label: 'Needs a person',  cls: 'bg-red-900/70 text-red-200' },
  done:         { label: 'Done via Paytrail', cls: 'bg-green-800 text-green-100' },
  sent:         { label: 'Sent — Paytrail e-mails the customer', cls: 'bg-blue-800 text-blue-100' },
  taken_over:   { label: 'Taken over',      cls: 'bg-gray-700 text-gray-300' },
};

function QueuedRefundsTab() {
  const [view, setView] = useState<'open' | 'history'>('open');
  const [q, setQ] = useState('');
  const [data, setData] = useState<any | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [errs, setErrs] = useState<Record<number, string>>({});
  const load = useCallback(() => {
    authFetch(`${R}/refund-queue?view=${view}&q=${encodeURIComponent(q.trim())}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setData).catch(() => setData({ jobs: [], counts: {}, view }));
  }, [view, q]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  const money = (n: number, cur?: string) => (!cur || cur === 'EUR') ? `€${n.toFixed(2)}` : `${n.toFixed(2)} ${cur}`;
  const act = async (id: number, what: 'retry' | 'take-over') => {
    if (what === 'take-over' && !window.confirm('Take this refund over? It leaves the queue and you refund it by hand from Manual refunds.')) return;
    setBusy(id); setErrs(e => ({ ...e, [id]: '' }));
    const res = await authFetch(`${R}/refund-queue/${id}/${what}`, { method: 'POST', body: '{}' });
    const d = await res.json().catch(() => ({}));
    setBusy(null);
    if (!res.ok) { setErrs(e => ({ ...e, [id]: (typeof d.detail === 'string' ? d.detail : d.detail?.message) || `HTTP ${res.status}` })); return; }
    load();
  };
  const counts = data?.counts || {};
  const tabBtn = (val: 'open' | 'history', label: string) => (
    <button onClick={() => setView(val)}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
        view === val ? 'bg-blue-600 border-blue-500 text-white' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'}`}>
      {label}
    </button>
  );
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {tabBtn('open', `Open (${(counts.queued || 0) + (counts.retry || 0) + (counts.needs_person || 0)})`)}
        {tabBtn('history', `History (${(counts.done || 0) + (counts.sent || 0) + (counts.taken_over || 0)})`)}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search order number or customer…"
          className="ml-auto px-3 py-1.5 rounded-lg bg-[#1a1d27] border border-gray-700 text-sm focus:outline-none focus:border-blue-500 w-72" />
      </div>
      <p className="text-gray-500 text-sm">
        Refunds the system pays through Paytrail when a return is accepted. When the Paytrail account holds no funds the job
        waits and tries again (2 h, 6 h, then every morning at 10:00). "Needs a person" means a rule stopped it — take it over
        and refund by hand from Manual refunds, or retry once the cause is fixed.
      </p>
      {!data ? <div className="text-gray-600 text-sm py-6">Loading…</div>
       : data.jobs.length === 0 ? (
        <div className="text-gray-600 text-sm py-10 text-center border border-dashed border-gray-800 rounded-lg">
          {view === 'open' ? 'Nothing waiting — every automatic refund is paid.' : 'No automatic refunds yet.'}
        </div>
      ) : data.jobs.map((j: any) => {
        const st = RQ_STATUS[j.status] || { label: j.status, cls: 'bg-gray-700 text-gray-200' };
        return (
          <div key={j.id} className="bg-[#1a1d27] border border-gray-800 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <OrderNo value={j.order_number} prefix="Order " className="font-semibold w-32" />
              {j.kind === 'cancellation' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/60 text-red-200" title="The customer cancelled the order — the whole payment is refunded">cancellation</span>}
              {j.kind === 'adjustment' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-900/60 text-sky-200" title="A refund override from the order page">override</span>}
              <span className="text-gray-400 w-28 truncate">{j.store_name}</span>
              <span className="text-gray-300 flex-1 truncate">{j.customer_name || '—'}</span>
              {j.provider && <span className="text-xs text-gray-500">{j.provider}</span>}
              {j.rcode && <span className="text-xs text-gray-500">{j.rcode}</span>}
              <span className="font-semibold">{money(j.amount, j.currency)}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${st.cls}`}
                title={j.last_error || undefined}>
                {st.label}
                {j.status === 'retry' && j.next_attempt_at ? ` — next try ${fmtWh(j.next_attempt_at, 'datetime')}` : ''}
                {j.status === 'done' && j.refunded_at ? ` ${fmtWh(j.refunded_at, 'datetime')}` : ''}
                {j.status === 'taken_over' && j.taken_over_by ? ` by ${j.taken_over_by}` : ''}
              </span>
              {view === 'open' && (
                <span className="flex gap-1">
                  <button disabled={busy === j.id} onClick={() => act(j.id, 'retry')}
                    className="px-3 py-1 rounded text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:opacity-50">Retry now</button>
                  <button disabled={busy === j.id} onClick={() => act(j.id, 'take-over')}
                    className="px-3 py-1 rounded text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-50">Take over</button>
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1 flex gap-4 flex-wrap">
              <span>queued {j.created_at ? fmtWh(j.created_at, 'datetime') : '—'}</span>
              <span>attempts {j.attempts}</span>
              {j.refund_transaction_id && <span>refund {j.refund_transaction_id}</span>}
              {j.last_error && <span className={j.status === 'needs_person' ? 'text-red-400' : 'text-amber-400'}>{j.last_error}</span>}
              {j.manual_processed && j.status !== 'done' && j.status !== 'sent' && <span className="text-gray-400">manual row processed by {j.manual_processed_by}</span>}
            </div>
            {errs[j.id] && <div className="text-xs text-red-400 mt-1">⚠ {errs[j.id]}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Exchange orders tab ───────────────────────────────────────────────────────
// The backend already works out, per exchange, whether the returned garment is
// physically back and shelved (`progress`) and therefore whether the correcting
// write is safe to fire (`can_process`). This tab used to ignore all of it: Process
// was always pressable, and because the response was thrown away, pressing it on an
// exchange whose item had not come back looked like nothing happening at all.
//
// So the state is shown, Process is only live when it will actually work, failures
// are said out loud, and Release finally has a button — the endpoint has existed
// since the leak was found, but with no way to call it two reservations sat stranded
// from July, holding stock off sale.  — Claude
const EX_STATE: Record<string, { label: string; cls: string; hint: string }> = {
  awaiting_return: { label: 'Waiting for the item', cls: 'bg-gray-700 text-gray-300',
    hint: 'The returned garment has not been accepted into a return order yet.' },
  unpolished: { label: 'Back, not polished', cls: 'bg-amber-600 text-white',
    hint: 'It has arrived but has not been through the polishing scan.' },
  polished: { label: 'Polished', cls: 'bg-blue-600 text-white',
    hint: 'Polished, but not yet shelved back into stock.' },
  replenished: { label: 'Ready to process', cls: 'bg-green-600 text-white',
    hint: 'The garment is back on the shelf. The correcting write can go ahead.' },
  no_return: { label: 'Customer keeps the original', cls: 'bg-gray-700 text-gray-300',
    hint: 'The customer keeps the original — nothing is coming back. Process opens once the replacement has shipped.' },
};

const EX_STATUS: Record<string, { label: string; cls: string; hint: string }> = {
  processed: { label: 'Processed', cls: 'bg-green-900/60 text-green-400',
    hint: 'The correcting stock write landed — the exchange completed.' },
  released: { label: 'Released', cls: 'bg-gray-700 text-gray-300',
    hint: 'The reservation was given back — the exchange did not go ahead.' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-900/50 text-red-400',
    hint: 'Closed administratively, no stock written.' },
  closed: { label: 'Closed', cls: 'bg-gray-700 text-gray-300',
    hint: 'Closed by a manager without a stock write — the customer has the replacement; the order was not changed in the store.' },
};

/**
 * Why Process is not available yet, in the order the flow actually blocks.
 *
 * The replacement must have physically gone (otherwise the store manager has
 * nothing to edit) AND the returned garment must be back on the shelf (the
 * correcting write assumes it is). Saying which of the two is missing saves the
 * reader guessing at a greyed-out button.  — Claude
 */
function processBlockedBecause(r: any): string | null {
  if (r.can_process) return null;
  if (!r.dispatched_at) {
    return r.pass_started_at
      ? 'The replacement is being picked and packed. Process opens once it has shipped.'
      : r.send_mode === 'on_return'
        ? 'Waiting for the original garment to arrive — accepting it sends the replacement.'
        : 'The replacement has not been sent yet.';
  }
  const st = r.progress?.state;
  if (st === 'replenished' || r.no_return) return null;
  return st === 'awaiting_return'
    ? 'Replacement sent. Waiting for the original garment to come back.'
    : 'Replacement sent. The original is back but not yet shelved.';
}

function ExchangeOrdersTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [showProcessed, setShowProcessed] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [errs, setErrs] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    authFetch(`${R}/exchange-requests?processed=${showProcessed}`)
      .then(r => r.json()).then(d => setRows(d.exchange_requests || []));
  }, [showProcessed]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // an exchange's returned item is named by the garment that comes BACK (server: returned_view → display)
  const names = (items: any[]) => (items || []).map(i => i.display || titleEn(i) || i.picklist || i.article_number).join(', ') || '—';
  const say = (id: number, msg: string) => setErrs(e => ({ ...e, [id]: msg }));

  const process = async (id: number) => {
    setBusy(id); setErrs(e => ({ ...e, [id]: '' }));
    try {
      const res = await authFetch(`${R}/exchange-requests/${id}/process`, { method: 'POST', body: '{}' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) say(id, d.detail || `Could not process (HTTP ${res.status})`);
      else load();
    } catch (e: any) {
      say(id, e.message || String(e));
    } finally { setBusy(null); }
  };

  // ONE way out (Fernando, 2026-09-17): Cancel exchange. Holds stock → the backend gives
  // it back (Release) and takes the order out of the pass; holds nothing → administrative
  // close (manager/admin, note required). Same button, same words, whatever the state.
  const [editing, setEditing] = useState<number | null>(null);
  const cancelExchange = async (r: any) => {
    const held = r.held_items?.length ? names(r.held_items) : '';
    const note = window.prompt(
      `Cancel exchange E${r.id} on order ${r.order_number}?\n\n` +
      (held ? `• ${held} goes back on sale\n` : '• nothing is reserved, so no stock is written\n') +
      (r.mid_pass ? `• the order leaves picking and returns to the tab it came from\n` : '') +
      `• the exchange is closed\n\n` +
      `Why is this exchange not going ahead?${held ? ' (optional)' : ' (required)'}`);
    if (note === null) return;
    if (!held && !note.trim()) { say(r.id, 'A note saying why is required.'); return; }
    setBusy(r.id); setErrs(e => ({ ...e, [r.id]: '' }));
    try {
      const res = await authFetch(`${R}/exchange-requests/${r.id}/cancel`,
        { method: 'POST', body: JSON.stringify({ note: note.trim() || null, abort_pass: true }) });
      const d = await res.json().catch(() => ({}));
      // 200 with ok:false = a stock write failed; the row stays open and a second press re-sends only what failed
      if (!res.ok) say(r.id, (typeof d.detail === 'string' ? d.detail : d.detail?.message) || `Could not cancel (HTTP ${res.status})`);
      else if (d.ok === false) {
        const why = (d.results || []).filter((x: any) => !x.ok).map((x: any) => `${x.article}: ${x.reason || '?'}`).join('; ');
        say(r.id, `Part of the stock was not given back, so the exchange stays open. Pressing Cancel again re-sends only what failed. ${why}`);
      } else load();
    } catch (e: any) {
      say(r.id, e.message || String(e));
    } finally { setBusy(null); }
  };

  // Once the replacement has shipped it is the customer's — never given back (Phase C, 28.10). A manager finalizes it
  // (change the order in the store, then Process) or, when the order stays as it is, closes it without a stock write.
  const manager = ['manager', 'admin'].includes(localStorage.getItem('wms_role') || '');
  const closeExchange = async (r: any) => {
    const note = window.prompt(
      `Close exchange E${r.id} on order ${r.order_number} without a stock write?\n\n` +
      `• the customer keeps the replacement; no stock is written\n` +
      `• only when the order is NOT changed in the store — after a store edit, press Process instead\n\n` +
      `Why? (required)`);
    if (note === null) return;
    if (!note.trim()) { say(r.id, 'A note saying why is required.'); return; }
    setBusy(r.id); setErrs(e => ({ ...e, [r.id]: '' }));
    try {
      const res = await authFetch(`${R}/exchange-requests/${r.id}/close`, { method: 'POST', body: JSON.stringify({ note: note.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) say(r.id, (typeof d.detail === 'string' ? d.detail : d.detail?.message) || `Could not close (HTTP ${res.status})`);
      else load();
    } catch (e: any) {
      say(r.id, e.message || String(e));
    } finally { setBusy(null); }
  };

  const header = (
    <div className="flex items-center justify-between">
      <p className="text-gray-500 text-sm">
        {showProcessed
          ? 'Exchanges that are closed — the correcting write landed, the reservation was released, or a manager closed it without a stock write.'
          : 'Customers wanting an exchange. Process writes the stock correction, and is only available once the returned garment is back on the shelf.'}
      </p>
      <button onClick={() => setShowProcessed(v => !v)}
        className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap ml-4">
        {showProcessed ? '← Open exchanges' : 'Show processed →'}
      </button>
    </div>
  );

  if (rows.length === 0) return (
    <div className="space-y-3">
      {header}
      <div className="text-gray-600 text-sm py-10 text-center border border-dashed border-gray-800 rounded-lg">
        {showProcessed ? 'No processed exchanges yet.' : 'No exchange orders to process.'}
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      {header}
      {rows.map(r => {
        const st = EX_STATE[r.progress?.state] || EX_STATE.awaiting_return;
        const p = r.progress || {};
        const stale = !r.processed && r.reserved && !r.dispatched_at && r.created_at &&
          (Date.now() - new Date(r.created_at).getTime()) > 14 * 864e5;
        return (
        <div key={r.id} className={`bg-[#1a1d27] border rounded-lg overflow-hidden ${
          stale ? 'border-amber-800/60' : 'border-gray-800'}`}>
          <div className="flex flex-wrap items-center gap-3 gap-y-1 px-4 py-3 cursor-pointer hover:bg-gray-800/30" onClick={() => toggle(r.id)}>
            <span className="text-gray-500 w-4">{open.has(r.id) ? '▾' : '▸'}</span>
            <OrderNo value={r.order_number} prefix="Order " className="font-semibold w-auto md:w-28" />
            <span className="text-gray-400 w-auto md:w-32 truncate">{r.store_name}</span>
            <span className="flex-1 text-gray-300 truncate">{names(r.wanted_items)}</span>
            {r.send_mode === 'on_return' && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400" title="The replacement is sent only once the old item is back">
                on return
              </span>
            )}
            {r.no_return && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400" title="The customer keeps the original — no return parcel is expected; if it comes back anyway it is not refunded">
                customer keeps it
              </span>
            )}
            {!r.processed && r.awaiting_store_edit && (
              <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-purple-600 text-white"
                title="The replacement has shipped. Change this order in the store, then press Process.">
                Change in store
              </span>
            )}
            {!r.processed && !r.awaiting_store_edit && r.pass_started_at && (
              <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-blue-700 text-white"
                title="The order is back in New carrying only the replacement, being picked and packed.">
                Sending replacement
              </span>
            )}
            {!r.processed && (
              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${st.cls}`} title={st.hint}>
                {st.label}
              </span>
            )}
            {!r.processed && (
              <>
                <button
                  disabled={!r.can_process || busy === r.id}
                  title={r.can_process
                    ? 'The order has been changed in the store: write the stock correction and close the exchange'
                    : (processBlockedBecause(r) || st.hint)}
                  onClick={e => { e.stopPropagation(); process(r.id); }}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    r.can_process ? 'bg-green-600 hover:bg-green-500 text-white'
                                  : 'bg-gray-800 text-gray-600 cursor-not-allowed'}`}>
                  Process
                </button>
                {r.partly_reserved && (
                  <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-red-900/60 text-red-200"
                    title={`Not every replacement could be reserved: ${(r.reserve_result || []).filter((x: any) => !x.ok).map((x: any) => `${x.variation_id}: ${x.reason || '?'}`).join('; ')}`}>
                    partly reserved
                  </span>
                )}
                {!r.dispatched_at && (
                  <button disabled={busy === r.id}
                    title="Change the returned or wanted items, the send mode or the reason. Stock follows the change."
                    onClick={e => { e.stopPropagation(); setEditing(v => v === r.id ? null : r.id); setOpen(s => new Set(s).add(r.id)); }}
                    className={`px-3 py-1 rounded text-xs font-medium ${editing === r.id ? 'bg-indigo-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'} disabled:opacity-50`}>
                    {editing === r.id ? 'Close editor' : 'Edit'}
                  </button>
                )}
                {(r.can_cancel ?? !r.dispatched_at) && (
                  <button disabled={busy === r.id}
                    title={(r.holds_stock ?? r.reserved)
                      ? 'Give the reserved piece(s) back to stock, take the order out of the pass and close this exchange'
                      : 'Close this exchange — nothing is reserved, so no stock is written (manager/admin)'}
                    onClick={e => { e.stopPropagation(); cancelExchange(r); }}
                    className="px-3 py-1 rounded text-xs font-medium bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 disabled:opacity-50">
                    Cancel exchange
                  </button>
                )}
                {r.can_close && manager && (
                  <button disabled={busy === r.id}
                    title="The customer has the replacement and the order is NOT changed in the store: close this exchange without a stock write (note required)"
                    onClick={e => { e.stopPropagation(); closeExchange(r); }}
                    className="px-3 py-1 rounded text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 disabled:opacity-50">
                    Close without stock write
                  </button>
                )}
              </>
            )}
            {r.processed && (() => {
              const chip = EX_STATUS[r.status] || EX_STATUS.processed;
              return (
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${chip.cls}`}
                    title={r.closed_note ? `${chip.hint} — ${r.closed_note}` : chip.hint}>
                    {chip.label}
                  </span>
                  <span className="text-xs text-gray-500">by {r.processed_by || '—'}</span>
                </span>
              );
            })()}
          </div>

          {editing === r.id && open.has(r.id) && (
            <div className="px-4 pb-4" onClick={e => e.stopPropagation()}>
              <ExchangeEditor r={r} onDone={() => { setEditing(null); load(); }} />
            </div>
          )}
          {errs[r.id] && (
            <div className="px-4 py-2 bg-red-900/30 border-t border-red-800 text-xs text-red-300">
              {errs[r.id]}
            </div>
          )}
          {!r.processed && r.awaiting_store_edit && !errs[r.id] && (
            <div className="px-4 py-2 bg-purple-950/40 border-t border-purple-800/60 text-xs text-purple-200">
              The replacement has shipped. Change this order in the store, then press
              Process — that writes the stock correction.
              {!r.can_process && ' Process stays locked until the returned garment is back on the shelf.'}
              {manager && ' If the order will not be changed in the store, close it without a stock write.'}
            </div>
          )}
          {!r.processed && !r.awaiting_store_edit && !errs[r.id] && processBlockedBecause(r) && (
            <div className="px-4 py-2 bg-gray-800/40 border-t border-gray-800 text-xs text-gray-400">
              {processBlockedBecause(r)}
            </div>
          )}
          {stale && !errs[r.id] && (
            <div className="px-4 py-2 bg-amber-900/20 border-t border-amber-900/50 text-xs text-amber-300">
              Reserved since {fmtWh(r.created_at, 'date')} — this piece has been
              off sale for over two weeks. Process it if the garment is back, release it if the
              exchange is not going ahead.
            </div>
          )}

          {open.has(r.id) && (
            <div className="border-t border-gray-800 px-4 py-3 text-sm grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div><div className="text-gray-500 text-xs mb-1">Returned</div><div className="text-gray-300">{names(r.returned_items)}</div></div>
              <div><div className="text-gray-500 text-xs mb-1">Wants instead</div><div className="text-gray-300">{names(r.wanted_items)}</div></div>
              <div><div className="text-gray-500 text-xs mb-1">Reason</div><div className="text-gray-300">{r.reason_text || '—'}</div></div>
              <div>
                <div className="text-gray-500 text-xs mb-1">Return progress</div>
                <div className="text-gray-300">
                  {r.no_return ? 'the customer keeps the original'
                    : p.expected ? `${p.shelved ?? 0} of ${p.expected} shelved` : 'not in a return order yet'}
                  {p.expected ? <span className="text-gray-500"> · {p.scanned ?? 0} polished</span> : null}
                </div>
                <div className="text-gray-500 text-xs mt-1">{st.hint}</div>
              </div>
              <div><div className="text-gray-500 text-xs mb-1">Send mode</div><div className="text-gray-300">{r.no_return ? 'Send now (nothing coming back)' : r.send_mode === 'on_return' ? 'Wait for the old item' : 'Send now'}</div></div>
              <div><div className="text-gray-500 text-xs mb-1">Reserved</div><div className="text-gray-300">{r.reserved ? 'yes — held off sale' : 'no'}</div></div>
              <div><div className="text-gray-500 text-xs mb-1">Created</div><div className="text-gray-300">{r.created_at ? fmtWh(r.created_at, 'datetime') : '—'}</div></div>
              <div><div className="text-gray-500 text-xs mb-1">Corrected</div><div className="text-gray-300">{r.corrected_at ? fmtWh(r.corrected_at, 'datetime') : '—'}</div></div>
              {r.closed_note && (
                <div className="col-span-2"><div className="text-gray-500 text-xs mb-1">Closing note</div><div className="text-gray-300">{r.closed_note}</div></div>
              )}
            </div>
          )}
        </div>
      );})}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Return order detail (/returns/order/:id) — items table + polishing scan session.
// ─────────────────────────────────────────────────────────────────────────────
export function ReturnOrderDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // on a P&P station the polisher signs in with their badge / PIN — every scan is recorded under the person (28.10)
  const sign = useDeskSignIn('returns_scan');
  const tt = returnsText(sign.who?.lang);
  const scanRef = useRef<HTMLInputElement>(null);
  const scanTimer = useRef<number | null>(null);
  const guard = useRef(makeScanGuard());

  const [data, setData] = useState<any | null>(null);
  const [flash, setFlash] = useState<string>('');
  // the polisher's feedback: green = counted, red = refused / the defects box (sound too)
  const [tone, setTone] = useState<'ok' | 'err' | 'info'>('info');
  // the piece scanned last — the Defect button (or the DEFECT card) sends it to the defects box
  const [lastLine, setLastLine] = useState<{ id: number; what: string } | null>(null);
  const tell = (t: 'ok' | 'err' | 'info', text: string, sound = true) => {
    setTone(t); setFlash(text);
    if (sound && t !== 'info') scanSound(t === 'ok' ? 'ok' : 'error');
  };
  const [busy, setBusy] = useState(false);
  // the same barcode on more than one line (two stores' parcels merged, or a return
  // line and an exchange line of one garment): the backend answers 'ambiguous' and
  // the worker picks the line the piece belongs to
  const [ambiguous, setAmbiguous] = useState<{ ean: string; candidates: any[] } | null>(null);

  const RO = `${R}/orders/${id}`;
  const load = useCallback(async () => {
    const res = await authFetch(RO);
    if (res.ok) setData(await res.json());
  }, [RO]);
  useEffect(() => { load(); }, [load]);

  const sessionId: number | null = data?.open_session_id ?? null;
  useEffect(() => {
    if (sign.asking) scanRef.current?.blur();              // the badge prompt reads the scanner itself (review 28.10)
    else if (sessionId) setTimeout(() => scanRef.current?.focus(), 50);
  }, [sessionId, sign.asking]);

  const startScan = async () => {
    setBusy(true);
    const res = await authFetch(`${RO}/scan/start`, { method: 'POST', body: '{}' });
    setBusy(false);
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    await load();
  };
  const saveScan = async () => {
    if (!sessionId) return;
    setBusy(true);
    const res = await authFetch(`${RO}/scan/save`, { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
    setBusy(false);
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    if (res.ok) { const d = await res.json(); tell('ok', tt.saved(d.items_scanned)); setData(d); setLastLine(null); }
    else { const e = await res.json().catch(() => ({})); setFlash(`⚠ ${e.detail || 'Could not save the session'}`); await load(); }
  };

  const submitScan = async (raw: string, lineId?: number) => {
    const ean = raw.trim();
    if (scanRef.current) scanRef.current.value = '';
    if (!ean || !sessionId || isBadgeCode(ean)) return;      // a badge is never a piece
    if (!lineId && guard.current(ean)) return;                // a double read of one pull counts once
    if (ean.toUpperCase().replace(/[^A-Z]/g, '') === 'DEFECT') {   // the DEFECT card: the last scanned piece is faulty
      if (lastLine) await markDefect(lastLine.id, lastLine.what); else tell('err', tt.nothingScanned);
      return;
    }
    setAmbiguous(null);
    setLastLine(null);       // the Defect button follows THIS piece only once it is counted (review 28.10)
    const res = await authFetch(`${RO}/scan`, { method: 'POST',
      body: JSON.stringify({ session_id: sessionId, ean, ...(lineId ? { line_id: lineId } : {}) }) });
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    const r = await res.json();
    if (!res.ok) tell('err', `⚠ ${typeof r.detail === 'string' ? r.detail : 'Scan failed'}`);
    else if (r.status === 'unknown') { tell('err', tt.notOnReturnOrder(ean)); setLastLine(null); }
    else if (r.status === 'ambiguous') {
      setAmbiguous({ ean, candidates: r.candidates || [] });
      tell('info', tt.whichParcel(ean), false);
      return;   // nothing recorded yet; the chooser below finishes the scan
    }
    else if (r.status === 'over') { tell('err', tt.overScan(r.picklist || r.article_number, r.qty_returned)); setLastLine(null); }
    else {
      const what = r.picklist || r.article_number || '';
      tell('ok', tt.counted(what, r.total_scanned, r.qty_returned));
      setLastLine({ id: r.line_id, what });
    }
    await load();
    scanRef.current?.focus();
  };
  const markDefect = async (lineId: number, what: string) => {
    setBusy(true);
    const res = await authFetch(`${RO}/lines/${lineId}/defect`, { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
    setBusy(false);
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { tell('err', `⚠ ${typeof d.detail === 'string' ? d.detail : 'Could not save'}`); return; }
    setData(d);
    setLastLine(null);
    tell('err', tt.defectsBox(what));        // red on purpose: the piece goes to the defects box, not the shelf
    scanRef.current?.focus();
  };
  const undoDefect = async (defectId: number) => {
    const res = await authFetch(`${R}/defects/${defectId}`, { method: 'DELETE' });
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { tell('err', `⚠ ${typeof d.detail === 'string' ? d.detail : 'Could not save'}`); return; }
    setData(d);
    tell('info', '', false);
  };
  const onScanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.currentTarget.value;
    if (scanTimer.current) window.clearTimeout(scanTimer.current);
    if (v.trim().length >= 4) scanTimer.current = window.setTimeout(() => submitScan(v), 90);
  };
  const onScanKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); if (scanTimer.current) window.clearTimeout(scanTimer.current); submitScan(e.currentTarget.value); }
  };

  // a piece the customer was told to keep came back and is held off sale (2026-10-03): a
  // manager puts it back on sale (stock written) or writes it off (nothing written, a note)
  const canDecideHeld = ['manager', 'admin'].includes(localStorage.getItem('wms_role') || '');
  const decideHeld = async (l: any, decision: 'on_sale' | 'write_off') => {
    let note: string | null = null;
    if (decision === 'write_off') {
      note = window.prompt(`Write off ${l.qty_returned}× ${l.article_number || titleEn(l)}?\n\nWhy? (required)`);
      if (note === null) return;
      if (!note.trim()) { setFlash('⚠ Say why it is written off.'); return; }
    } else if (!window.confirm(`Put ${l.qty_returned}× ${l.article_number || titleEn(l)} back on sale?\n\nThe stock is written back now — then put the piece on its shelf${l.stockplace ? ` (${l.stockplace})` : ''}.`)) return;
    setBusy(true);
    const res = await authFetch(`${RO}/lines/${l.id}/held`, { method: 'POST', body: JSON.stringify({ decision, note: note?.trim() || null }) });
    setBusy(false);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setFlash(`⚠ ${typeof d.detail === 'string' ? d.detail : 'Could not save the decision'}`); return; }
    setFlash(decision === 'on_sale' ? `✓ Back on sale (${d.target || 'stock written'}) — put it on the shelf` : '✓ Written off');
    setData(d);
  };

  if (!data) return <div className="p-6 text-gray-500">Loading…</div>;
  const ro = data.return_order;
  const canReplenish = data.lines.some((l: any) => (l.replenishable || 0) > 0);

  const archive = async () => {
    if (!window.confirm('Have you scanned everything in this return order?')) return;
    if (!window.confirm('Have you put all the items from this return order back to the shelf?')) return;
    if (!window.confirm(`Are you sure you want to archive ${ro.code}? This moves it to the archive.`)) return;
    const res = await authFetch(`${RO}/archive`, { method: 'POST', body: '{}' });
    if (res.ok) navigate('/returns/list');
    else { const e = await res.json().catch(() => ({})); setFlash(e.detail || 'Could not archive'); }
  };

  const rowTone = (l: any) => {
    const total = (l.qty_scanned || 0) + (l.ongoing || 0);
    if (total === 0) return '';
    if (total > l.qty_returned) return 'bg-red-900/30';
    if (total === l.qty_returned) return 'bg-green-900/30';
    return 'bg-amber-900/25';
  };

  return (
    <div className="p-6 text-gray-200 max-w-7xl mx-auto">
      {sign.box}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate('/returns/list')} className="text-gray-500 hover:text-white text-sm">← Returns</button>
        {sign.chip}
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-2xl font-semibold">{ro.code}</span>
          <span className="ml-3 text-sm text-gray-500">{ro.status}</span>
        </div>
        <div className="flex gap-2">
          {!sessionId ? (
            <button onClick={startScan} disabled={busy || ro.status !== 'active'}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 font-medium text-sm">{tt.startScanning}</button>
          ) : (
            <button onClick={saveScan} disabled={busy}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 font-medium text-sm">{tt.saveScanning}</button>
          )}
          <button onClick={() => navigate(`/returns/order/${id}/replenish`)} disabled={!canReplenish || ro.status !== 'active'}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm" title={canReplenish ? '' : 'Scan & save items first'}>Start replenishment</button>
          <button onClick={archive} disabled={ro.status !== 'active'}
            className="px-4 py-2 rounded-lg bg-rose-700 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm">Archive</button>
        </div>
      </div>

      {flash && <div className={`mb-4 px-4 py-3 rounded-lg border-2 text-lg font-medium ${tone === 'ok' ? 'bg-green-900/60 border-green-600 text-green-100'
        : tone === 'err' ? 'bg-red-900/70 border-red-500 text-red-100' : 'bg-gray-800 border-gray-600 text-gray-200'}`}>{flash}</div>}

      {sessionId && (
        <div className="mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={scanRef} autoFocus placeholder={tt.scanPolished} onChange={onScanChange} onKeyDown={onScanKey}
              className="w-full max-w-md px-4 py-3 rounded-lg bg-[#1a1d27] border border-blue-700 text-lg focus:outline-none focus:border-blue-400" />
            {lastLine && (
              <button onMouseDown={e => e.preventDefault()} onClick={() => markDefect(lastLine.id, lastLine.what)} disabled={busy}
                className="px-4 py-3 rounded-lg bg-red-700 hover:bg-red-600 text-white text-lg font-semibold disabled:opacity-40">
                {tt.defectButton(lastLine.what)}
              </button>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-500">{tt.polishingHelp}</div>
          {ambiguous && (
            <div className="mt-3 max-w-2xl rounded-lg border border-amber-700/60 bg-amber-950/30 p-3">
              <div className="text-sm text-amber-200 mb-2">
                {ambiguous.ean} is on more than one line. Which parcel did this piece come from?
              </div>
              <div className="space-y-1">
                {ambiguous.candidates.map((c: any) => (
                  <button key={c.line_id} onClick={() => submitScan(ambiguous.ean, c.line_id)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm">
                    <span className="font-medium">{(c.order_ids || []).join(', ') || '—'}</span>
                    <span className="text-gray-400">{c.store_name}</span>
                    <span className="text-gray-500 text-xs">{c.source_kind}</span>
                    <span className="flex-1 text-gray-400 truncate">{titleEn(c)}</span>
                    <span className="tabular-nums text-gray-300">{c.scanned}/{c.qty_returned}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => { setAmbiguous(null); setFlash(''); scanRef.current?.focus(); }}
                className="mt-2 text-xs text-gray-500 hover:text-white">Cancel this scan</button>
            </div>
          )}
        </div>
      )}

      <table className="w-full text-sm bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
        <thead className="text-gray-500 text-left border-b border-gray-800">
          <tr>
            <th className="py-2 px-3">Order no.</th>
            <th className="py-2 px-3">Article number</th>
            <th className="py-2 px-3">EAN</th>
            <th className="py-2 px-3">Stock loc.</th>
            <th className="py-2 px-3">Pick list no.</th>
            <th className="py-2 px-3">Product</th>
            <th className="py-2 px-3 text-center">Returned</th>
            <th className="py-2 px-3 text-center">Received</th>
            <th className="py-2 px-3 text-center">Scanned &amp; saved</th>
            <th className="py-2 px-3 text-center">Ongoing</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((l: any) => (
            <tr key={l.id} id={`rol-${l.id}`} className={`border-b border-gray-800/50 ${rowTone(l)}`}>
              <td className="py-2 px-3 text-gray-400">
                {(l.order_ids || []).map((oid: any, k: number) => (
                  <React.Fragment key={k}>{k > 0 && ', '}<OrderNo value={oid} /></React.Fragment>
                ))}
              </td>
              <td className="py-2 px-3 font-medium">
                {l.article_number}
                {l.ordered_variation_id && (
                  <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200"
                        title="Wrong item shipped: this is the garment that came back, booked as itself. The variant it was sold as stays one too low in the shop until corrected by hand — see Review → Wrong item packed.">
                    wrong item shipped
                  </span>
                )}
                {l.held && (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${l.held.decision ? 'bg-gray-800 text-gray-400' : 'bg-teal-900 text-teal-100'}`}
                          title="The customer was told to keep this piece and it came back anyway: no refund. Its reason holds it off sale until a manager decides.">
                      {l.held.decision === 'on_sale' ? 'kept piece — put back on sale'
                        : l.held.decision === 'written_off' ? 'kept piece — written off'
                        : l.held.decision === 'mixed' ? 'kept pieces — partly back on sale, partly written off'
                        : 'held off sale — kept by the customer'}{l.held.note ? ` · ${l.held.note}` : ''}
                    </span>
                    {l.held.decision && (
                      <span className="text-[11px] text-gray-500">{l.held.decided_by} · {l.held.decided_at ? fmtWh(l.held.decided_at, 'short') : ''}{l.held.decision_note ? ` — ${l.held.decision_note}` : ''}</span>
                    )}
                    {!l.held.decision && canDecideHeld && (
                      <>
                        <button disabled={busy} onClick={() => decideHeld(l, 'on_sale')}
                          className="text-[11px] px-2 py-0.5 rounded bg-green-800 hover:bg-green-700 text-green-100 disabled:opacity-40">Put back on sale</button>
                        <button disabled={busy} onClick={() => decideHeld(l, 'write_off')}
                          className="text-[11px] px-2 py-0.5 rounded bg-gray-800 hover:bg-red-900/60 text-gray-300 disabled:opacity-40">Write off</button>
                      </>
                    )}
                    {!l.held.decision && !canDecideHeld && <span className="text-[11px] text-gray-500">a manager decides — put it aside, not on the shelf</span>}
                  </div>
                )}
                {(l.defects || []).length > 0 && l.source_kind !== 'kept' && (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-red-900/70 text-red-100">
                      {tt.inDefectsBox((l.defects || []).filter((d: any) => d.decision !== 'on_sale').length)}
                    </span>
                    {(l.defects || []).filter((d: any) => !d.decision && d.source === 'polishing').map((d: any) => (
                      <button key={d.id} onClick={() => undoDefect(d.id)} className="text-[11px] text-gray-400 underline hover:text-white">
                        {tt.undoDefect}
                      </button>
                    ))}
                  </div>
                )}
                {!l.wgr_matched && <span className="ml-2 text-amber-500 text-xs">· not in WGR</span>}
                {l.wgr_ambiguous && (
                  <span className="ml-2 text-amber-500 text-xs"
                    title="This EAN maps to more than one WGR article (e.g. two sizes share a barcode). The pick is deterministic but may be the wrong size — check the garment before saving.">
                    · shared EAN — check size
                  </span>
                )}
              </td>
              <td className="py-2 px-3 text-gray-500">{l.ean}</td>
              <td className="py-2 px-3">{l.stockplace || '—'}</td>
              <td className="py-2 px-3 text-gray-300">{l.picklist}</td>
              <td className="py-2 px-3 text-gray-400">{titleEn(l)}</td>
              <td className="py-2 px-3 text-center font-semibold">{l.qty_returned}</td>
              <td className="py-2 px-3 text-center text-gray-400">{l.qty_received}</td>
              <td className="py-2 px-3 text-center">{l.qty_scanned}</td>
              <td className="py-2 px-3 text-center font-semibold">{l.ongoing || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Replenishment (/returns/order/:id/replenish) — picking-style walk for the cart+
// tablet, then a confirm page to save the replenished sections back to stock.
// ─────────────────────────────────────────────────────────────────────────────
export function ReturnReplenish() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const RO = `${R}/orders/${id}`;
  // on a P&P station the walker signs in with their badge / PIN — the walk is recorded under the person (28.10)
  const sign = useDeskSignIn('replenish');

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [rows, setRows] = useState<any[]>([]);          // route-ordered lines
  const [phase, setPhase] = useState<'loading' | 'walk' | 'confirm' | 'done'>('loading');
  const [stopIdx, setStopIdx] = useState(0);
  const [reached, setReached] = useState(0);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<any | null>(null);
  // set when the store's stock system is unreachable — blocks the walk entirely
  const [offline, setOffline] = useState<{ message: string; detail: string;
                                           systems?: string; stores?: string } | null>(null);
  // Å shelves (1.11, docs/PIECE-PLACES.md): a returned Ä piece or one with no shelf goes to an Å shelf, not back to
  // its product's place — the server proposes one per line (fewest pieces), the walker can tap another
  const [aaPlaces, setAaPlaces] = useState<{ code: string; display: string; pieces: number }[]>([]);
  const [aaChoice, setAaChoice] = useState<Record<number, string>>({});
  const aaLabel = (c: string) => aaPlaces.find(p => p.code === c)?.display || c;
  const placeOf = (r: any) => (r.aa_place ? `→ ${aaLabel(aaChoice[r.line_id] || r.aa_place)}` : r.stockplace);

  // group route-ordered rows into stops by stockplace (order preserved); Å rows by their chosen shelf
  const stops: { stockplace: string; rows: any[] }[] = [];
  rows.forEach(r => {
    const key = placeOf(r);
    const last = stops[stops.length - 1];
    if (last && last.stockplace === key) last.rows.push(r);
    else stops.push({ stockplace: key, rows: [r] });
  });

  useEffect(() => {
    if (!sign.who || (sign.who.station && !sign.who.person)) return;   // who is walking is known first (review 28.10)
    (async () => {
      const res = await authFetch(`${RO}/replenish/start`, { method: 'POST', body: '{}' });
      if (await isBadgeRequired(res)) { sign.askAgain(); return; }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        // 503 carries a structured detail: the store's stock system is unreachable, so
        // the walk is refused up front rather than failing at Save after the whole
        // shelf run. Anything else is a plain string.
        if (res.status === 503 && e.detail && typeof e.detail === 'object') { setOffline(e.detail); return; }
        setErr(typeof e.detail === 'string' ? e.detail : 'Could not start');
        return;
      }
      const d = await res.json();
      setSessionId(d.session_id);
      setRows(d.rows || []);
      setAaPlaces(d.aa_places || []);
      setAaChoice(Object.fromEntries((d.rows || []).filter((r: any) => r.aa_place).map((r: any) => [r.line_id, r.aa_place])));
      setPhase('walk');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RO, sign.who?.person, sign.who?.station]);   // a station: starts once the walker has signed in

  // map each line_id -> its stop index (for pre-ticking up to where they reached)
  const stopOfLine: Record<number, number> = {};
  stops.forEach((s, i) => s.rows.forEach(r => { stopOfLine[r.line_id] = i; }));

  const goConfirm = (reachedIndex: number) => {
    setReached(reachedIndex);
    const pre = new Set<number>(rows.filter(r => stopOfLine[r.line_id] <= reachedIndex).map(r => r.line_id));
    setTicked(pre);
    setPhase('confirm');
  };

  const toggle = (lid: number) => setTicked(s => { const n = new Set(s); n.has(lid) ? n.delete(lid) : n.add(lid); return n; });

  const save = async () => {
    if (!sessionId) return;
    setBusy(true);
    const aa_places = Object.fromEntries(rows.filter(r => r.aa_place && ticked.has(r.line_id)).map(r => [String(r.line_id), aaChoice[r.line_id] || r.aa_place]));
    const res = await authFetch(`${RO}/replenish/save`, { method: 'POST', body: JSON.stringify({ session_id: sessionId, line_ids: Array.from(ticked), aa_places }) });
    setBusy(false);
    if (await isBadgeRequired(res)) { sign.askAgain(); return; }
    if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(e.detail || 'Save failed'); return; }
    const d = await res.json();
    setSummary(d);
    setPhase('done');
  };

  if (err) return (
    <div className="p-6 text-gray-200">
      <div className="mb-4 text-amber-400">{err}</div>
      <button onClick={() => navigate(`/returns/order/${id}`)} className="px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm">← Back to return order</button>
    </div>
  );
  // Blocks the walk before it starts. Deliberately not dismissable into the walk —
  // the only way on is back out, because saving would fail and the worker would have
  // done the whole shelf run for nothing.
  if (offline) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
      <div className="max-w-lg w-full rounded-xl border-2 border-red-600 bg-gray-900 p-6 text-center">
        <div className="text-5xl mb-3">⚠️</div>
        <h2 className="text-xl font-bold text-red-500 mb-3">{offline.message}</h2>
        <p className="text-gray-300 text-sm mb-5">{offline.detail}</p>
        <div className="flex gap-2 justify-center">
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium">
            Try again
          </button>
          <button onClick={() => navigate(`/returns/order/${id}`)}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">
            Back to the return order
          </button>
        </div>
      </div>
    </div>
  );

  if (phase === 'loading') return <div className="p-6 text-gray-500">{sign.box}Loading…</div>;

  // ── always-on banner so workers don't mistake this for picking
  const Banner = () => (
    <div className="bg-indigo-700 text-white text-center py-2 font-semibold tracking-wide text-sm">REPLENISHMENT SESSION</div>
  );

  if (phase === 'walk') {
    const stop = stops[stopIdx];
    const last = stopIdx >= stops.length - 1;
    return (
      <div className="flex flex-col h-full">
        <Banner />
        <div className="flex-1 flex flex-col items-center justify-center text-gray-200 px-6">
          <div className="text-gray-500 text-sm mb-2">Stop {stopIdx + 1} of {stops.length}</div>
          <div className="text-6xl font-bold mb-6">{stop.stockplace || '—'}</div>
          <div className="w-full max-w-lg space-y-2">
            {stop.rows.map(r => (
              <div key={r.line_id} className="flex items-center justify-between bg-[#1a1d27] border border-gray-800 rounded-lg px-4 py-3">
                <div>
                  <div className="font-medium">{r.picklist}</div>
                  <div className="text-xs text-gray-500">{titleEn(r)}{r.wgr_matched === false && <span className="ml-2 text-amber-500">· not in WGR</span>}</div>
                </div>
                <div className="text-2xl font-bold">×{r.replenish_qty}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3 p-4">
          <button onClick={() => goConfirm(stopIdx)} className="flex-1 py-5 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg font-semibold">End here</button>
          <button onClick={() => last ? goConfirm(stops.length - 1) : setStopIdx(stopIdx + 1)} className="flex-1 py-5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-lg font-semibold">{last ? 'Done' : 'Continue →'}</button>
        </div>
      </div>
    );
  }

  if (phase === 'confirm') {
    return (
      <div className="flex flex-col h-full">
        {sign.box}
        <Banner />
        <div className="p-6 text-gray-200 max-w-3xl mx-auto w-full">
          <h2 className="text-lg font-semibold mb-1">Confirm replenished sections</h2>
          <p className="text-gray-500 text-sm mb-4">Tick the stock locations where you put the pieces back.</p>
          <table className="w-full text-sm bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
            <thead className="text-gray-500 text-left border-b border-gray-800">
              <tr><th className="py-2 px-3 w-10"></th><th className="py-2 px-3">Stock location</th><th className="py-2 px-3">Pick list no.</th><th className="py-2 px-3 text-center">Qty</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.line_id} className={`border-b border-gray-800/50 ${ticked.has(r.line_id) ? 'bg-indigo-900/20' : ''}`}>
                  <td className="py-2 px-3"><input type="checkbox" checked={ticked.has(r.line_id)} onChange={() => toggle(r.line_id)} /></td>
                  <td className="py-2 px-3">
                    {r.aa_place ? (
                      // an Ä piece / no shelf: on an Å shelf — the proposal is lit, tap another to change it
                      <span className="inline-flex items-center gap-1">
                        <span className="text-gray-500 text-xs mr-1">{r.stockplace || 'no shelf'} →</span>
                        {aaPlaces.map(p => <button key={p.code} type="button" onClick={() => setAaChoice(c => ({ ...c, [r.line_id]: p.code }))}
                          className={`px-2 py-1 rounded text-xs font-bold ${(aaChoice[r.line_id] || r.aa_place) === p.code ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-200'}`}>{p.display}</button>)}
                      </span>
                    ) : (r.stockplace || '—')}
                  </td>
                  <td className="py-2 px-3 text-gray-300">{r.picklist}</td>
                  <td className="py-2 px-3 text-center font-semibold">{r.replenish_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between mt-4">
            <button onClick={() => setPhase('walk')} className="px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm">← Back to walk</button>
            <button onClick={save} disabled={busy || ticked.size === 0} className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 font-medium">Save replenishment session ({ticked.size})</button>
          </div>
        </div>
      </div>
    );
  }

  // done
  const failed = summary?.failed || [];
  const recovered = (summary?.results || []).filter((r: any) => r.recovered);
  return (
    <div className="p-6 text-gray-200 max-w-2xl mx-auto">
      <h2 className="text-lg font-semibold mb-2">Replenishment saved</h2>
      <p className="text-gray-400 mb-3">Added {summary?.replenished ?? 0} item(s) back to stock.</p>
      {/* say WHY a line was not written, and whether Review has it — a bare
          "could not be written" left the worker with nothing to act on */}
      {failed.length > 0 && (
        <div className="mb-3 px-4 py-3 rounded bg-red-900/30 text-sm">
          <div className="font-medium mb-1">{failed.length} item(s) were NOT written to stock — they stay in this return order:</div>
          <ul className="space-y-0.5">
            {failed.map((f: any) => (
              <li key={f.line_id}>
                <span className="font-medium">{f.article_number}</span> ×{f.qty}
                <span className="text-gray-400"> → {f.target}: {f.reason || 'no reason given'}</span>
                {f.needs_review && <span className="ml-2 text-amber-400 text-xs">queued in Review</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {recovered.length > 0 && (
        <div className="mb-3 px-4 py-2 rounded bg-blue-900/30 text-sm text-blue-200">
          {recovered.map((r: any) => `${r.article_number} ×${r.qty}`).join(', ')} had already reached the shop on an
          earlier save and {recovered.length === 1 ? 'was' : 'were'} recorded now without being sent again.
        </div>
      )}
      <button onClick={() => navigate(`/returns/order/${id}`)} className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-sm font-medium">← Back to return order</button>
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────
// The customer return portal per store (Returns project 2 Phase E, 28.10): Off / Test / Live, the store's own site
// addresses (the only pages that may use it), and the snippet to paste into a page of the shop. Test = everything
// works and nothing is saved or held; Live registers for real (sizes are held) — Fernando's word only.
function PortalSettings({ value, save }: { value: string; save: (v: string) => void }) {
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [copied, setCopied] = useState('');
  useEffect(() => {
    authFetch(`${API}/settings/webshops`).then(r => r.ok ? r.json() : []).then(d => setStores((d || []).map((s: any) => ({ id: s.id, name: s.name }))));
  }, []);
  let cfg: Record<string, { mode: string; origins: string[] }> = {};
  try { cfg = JSON.parse(value || '{}') || {}; } catch { cfg = {}; }
  const blank = { mode: 'off', origins: [] as string[] };
  const setShop = (id: string, patch: Partial<{ mode: string; origins: string[] }>) =>
    save(JSON.stringify({ ...cfg, [id]: { ...blank, ...cfg[id], ...patch } }));
  const snippet = (id: string) =>
    `<div id="wms-returns" data-shop="${id}"></div>\n<script src="${window.location.origin}/api/portal/v1/embed.js" async></script>`;
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
      <div className="font-medium mb-1">Customer return portal</div>
      <p className="text-gray-500 text-sm mb-3">The return form customers fill in on the shop's own page (order number + e-mail; a free size exchange or a refund). <b>Test</b>: everything works and nothing is saved or held. <b>Live</b>: registrations are real and an exchange holds its size for 21 days.</p>
      <div className="space-y-4">
        {stores.map(s => {
          const c = cfg[s.id] || { mode: 'off', origins: [] };
          return (
            <div key={s.id} className="border-t border-gray-800 pt-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-200 w-36">{s.name}</span>
                {(['off', 'test', 'live'] as const).map(m => (
                  <button key={m} onClick={() => {
                    if (m === 'live' && !window.confirm(`Switch the ${s.name} return portal to LIVE?\n\nCustomers' registrations become real and an exchange holds its size for 21 days.`)) return;
                    setShop(s.id, { mode: m });
                  }}
                    className={`px-3 py-1 rounded text-xs ${c.mode === m ? (m === 'live' ? 'bg-green-700 text-white' : m === 'test' ? 'bg-amber-700 text-white' : 'bg-gray-600 text-white') : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    {m === 'off' ? 'Off' : m === 'test' ? 'Test' : 'Live'}
                  </button>
                ))}
              </div>
              <label className="block text-xs text-gray-500 mt-2">The shop's own site addresses, one per line (https://…, no path)</label>
              <textarea defaultValue={(c.origins || []).join('\n')} rows={2}
                onBlur={e => setShop(s.id, { origins: e.target.value.split(/\s+/).map(x => x.trim().replace(/\/+$/, '')).filter(Boolean) })}
                className="w-full mt-1 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-xs text-gray-200 font-mono" />
              {c.mode !== 'off' && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">Paste into a page of the shop (Shopify: a "Custom liquid" block):</div>
                  <pre className="text-[11px] bg-gray-950 border border-gray-800 rounded p-2 overflow-x-auto text-gray-300">{snippet(s.id)}</pre>
                  <button onClick={() => { navigator.clipboard?.writeText(snippet(s.id)); setCopied(s.id); setTimeout(() => setCopied(''), 1500); }}
                    className="mt-1 text-xs text-blue-400 hover:text-blue-300">{copied === s.id ? 'Copied' : 'Copy'}</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReturnsSettings() {
  const [settings, setSettings] = useState<Record<string, string> | null>(null);
  const [saved, setSaved] = useState(false);
  const [stats, setStats] = useState<any | null>(null);

  useEffect(() => { authFetch(`${R}/settings`).then(r => r.json()).then(d => setSettings(d.settings || {})); }, []);
  useEffect(() => { authFetch(`${R}/stats/full-return`).then(r => r.json()).then(setStats).catch(() => {}); }, []);

  const update = async (key: string, value: string) => {
    const before = settings?.[key];
    setSettings(s => ({ ...(s || {}), [key]: value }));
    const res = await authFetch(`${R}/settings`, { method: 'PUT', body: JSON.stringify({ [key]: value }) });
    if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); }
    else {
      // a rejected save must not look saved (review LOW)
      const d = await res.json().catch(() => ({}));
      setSettings(s => ({ ...(s || {}), [key]: before ?? '' }));
      alert(typeof d.detail === 'string' ? d.detail : 'Could not save the setting');
    }
  };

  if (!settings) return <div className="text-gray-600 text-sm py-8">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <PortalSettings value={settings.portal || '{}'} save={v => update('portal', v)} />
      <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
        <div className="font-medium mb-1">Default returned quantity on Accept Returns</div>
        <p className="text-gray-500 text-sm mb-3">When an order is opened on the Accept Returns page, prefill each line's returned quantity as empty, or as the full ordered quantity (often the whole parcel comes back).</p>
        <div className="flex gap-2">
          {([['zero', 'Empty (0)'], ['full', 'Full ordered quantity']] as const).map(([val, label]) => (
            <button key={val} onClick={() => update('accept_default_qty', val)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                (settings.accept_default_qty || 'zero') === val
                  ? 'bg-blue-600 border-blue-500 text-white'
                  : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* live tracker: how often the whole order is returned */}
        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Full-order returns (live)</div>
          {!stats ? (
            <div className="text-gray-600 text-sm">Loading…</div>
          ) : (
            <div className="flex gap-8">
              {([['Last 30 days', stats.last_30], ['All time', stats.all_time]] as const).map(([label, s]: any) => (
                <div key={label}>
                  <div className="text-2xl font-semibold">{s.pct === null ? '—' : `${s.pct}%`}</div>
                  <div className="text-xs text-gray-500">{label}{s.total ? ` · ${s.full}/${s.total} returns` : ''}</div>
                </div>
              ))}
            </div>
          )}
          <p className="text-gray-600 text-xs mt-2">If most returns are full-order, the “Full ordered quantity” default saves marking time.</p>
        </div>
      </div>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
        <div className="font-medium mb-1">Where returns are accepted</div>
        <p className="text-gray-500 text-sm mb-3">A store whose returns are handled in its own shop system is refused on the Accept Returns page with a red notice. Motley Denim stays in WGR until WGR delivers the API pieces (2026-09-16).</p>
        <div className="space-y-2">
          {([['wgr', 'Motley Denim (WGR)'], ['mcf', 'Big Oliver (MyCashflow)'], ['shopify', 'Nora Mikken (Shopify)']] as const).map(([plat, label]) => {
            const blocked = (settings.accept_in_shop_platforms || '').split(',').filter(Boolean);
            const isBlocked = blocked.includes(plat);
            const set = (v: boolean) => update('accept_in_shop_platforms', (v ? Array.from(new Set([...blocked, plat])) : blocked.filter(p => p !== plat)).join(','));
            return (
              <div key={plat} className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-300">{label}</span>
                <div className="flex gap-1">
                  <button onClick={() => set(false)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!isBlocked ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>In the WMS</button>
                  <button onClick={() => set(true)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${isBlocked ? 'bg-red-700 border-red-600 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>In the shop (blocked here)</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
        <div className="font-medium mb-1">Automatic refunds via Paytrail <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/70 text-emerald-300 align-middle">AUTO</span></div>
        <p className="text-gray-500 text-sm mb-3">For a store switched on, every accepted return's refund is queued and paid through Paytrail by the system (Queued refunds tab). The store needs its Paytrail merchant id and secret in Settings → Shipping → the store → General. Stores switched off stay on Manual refunds.</p>
        <div className="space-y-2">
          {([['big_oliver', 'Big Oliver (MyCashflow · Paytrail)'], ['nora_mikken', 'Nora Mikken (Shopify · MakeCommerce — not supported yet)'], ['motley_denim', 'Motley Denim (WGR — returns stay in WGR)']] as const).map(([ws, label]) => {
            const on = (settings.auto_refund_webshops || '').split(',').filter(Boolean);
            const isOn = on.includes(ws);
            const set = (v: boolean) => update('auto_refund_webshops', (v ? Array.from(new Set([...on, ws])) : on.filter(p => p !== ws)).join(','));
            const supported = ws === 'big_oliver';
            return (
              <div key={ws} className="flex items-center justify-between gap-3">
                <span className={`text-sm ${supported ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
                <div className="flex gap-1">
                  <button onClick={() => set(false)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!isOn ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>Manual</button>
                  <button disabled={!supported} onClick={() => set(true)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-40 ${isOn ? 'bg-emerald-700 border-emerald-600 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>Automatic</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
        <div className="font-medium mb-1">EUR rates for the Unclaimed COD table</div>
        <p className="text-gray-500 text-sm mb-3">Units of each currency per 1 EUR (ECB reference rates). A case registered for an order in SEK, CZK… gets its EUR value from these; the rate used is stored on the case. Update them now and then — they are not fetched automatically.</p>
        <EurRatesEditor value={settings.eur_rates || ''} onSave={v => update('eur_rates', v)} />
      </div>
      {saved && <div className="text-green-400 text-sm">✓ Saved</div>}

      <RefundRules />
      <ReasonsManager />
    </div>
  );
}

// ── return shipping fee + reclaim settings (per platform / per store) ─────────
// Mirrors backend compute_refund so the verification table is live & truthful.

// ── reasons CRUD (return + exchange) ──────────────────────────────────────────
function ReasonsManager() {
  const [reasons, setReasons] = useState<any[]>([]);
  const [newLabel, setNewLabel] = useState<Record<string, string>>({ return: '', exchange: '', resend: '' });
  const [err, setErr] = useState('');

  const load = useCallback(() => { authFetch(`${R}/reasons`).then(r => r.json()).then(d => setReasons(d.reasons || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (kind: string) => {
    const label = (newLabel[kind] || '').trim();
    if (!label) return;
    setErr('');
    const res = await authFetch(`${R}/reasons`, { method: 'POST', body: JSON.stringify({ kind, label }) });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(e.detail || 'Could not add the reason'); return; }
    setNewLabel(s => ({ ...s, [kind]: '' }));
    load();
  };
  const toggle = async (r: any) => {
    const res = await authFetch(`${R}/reasons/${r.id}`, { method: 'PATCH', body: JSON.stringify({ active: !r.active }) });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(e.detail || 'Could not change the reason'); }
    load();
  };
  // a piece the customer was told to keep that comes back anyway: held off sale (true) or restocked
  const toggleHold = async (r: any) => {
    const res = await authFetch(`${R}/reasons/${r.id}`, { method: 'PATCH', body: JSON.stringify({ kept_hold: !r.kept_hold }) });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(e.detail || 'Could not change the reason'); }
    load();
  };
  const del = async (id: number) => {
    if (!window.confirm('Delete this reason?')) return;
    const res = await authFetch(`${R}/reasons/${id}`, { method: 'DELETE' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(e.detail || 'Could not delete the reason'); }
    load();
  };

  const group = (kind: string, title: string) => (
    <ReasonGroup key={kind} kind={kind} title={title}
      reasons={reasons.filter(r => r.kind === kind)} draft={newLabel[kind] || ''}
      onDraft={v => setNewLabel(s => ({ ...s, [kind]: v }))} onAdd={() => add(kind)} onToggle={toggle} onDelete={del}
      onToggleHold={kind === 'return' ? undefined : toggleHold} />
  );

  return (
    <div>
      {err && <div className="mb-3 px-3 py-2 rounded bg-red-900/40 text-red-300 text-sm">⚠ {err}</div>}
      <div className="grid grid-cols-3 gap-4">
        {group('exchange', 'Exchange reasons')}
        {group('return', 'Return reasons')}
        {/* offered by the order panel's "Create a resend order" dialog */}
        {group('resend', 'Resend reasons')}
      </div>
      <p className="mt-3 text-xs text-gray-500">
        <span className="text-teal-300">hold</span> — when the customer was told to keep the item (exchange or resend) and it
        comes back anyway, nobody is refunded; with a <span className="text-teal-300">hold</span> reason the piece is kept off sale
        until a manager puts it back on sale or writes it off (Review → Kept piece came back). Without it, the piece is restocked.
      </p>
    </div>
  );
}

// A module-level component, not a closure inside ReasonsManager: a component
// defined inside render is a NEW type on every render, so React unmounted and
// remounted the whole group on each keystroke — the "New reason…" box lost
// focus after every character typed. (F1, 2026-09-09)
function ReasonGroup({ kind, title, reasons, draft, onDraft, onAdd, onToggle, onDelete, onToggleHold }: {
  kind: string; title: string; reasons: any[]; draft: string;
  onDraft: (v: string) => void; onAdd: () => void; onToggle: (r: any) => void; onDelete: (id: number) => void;
  onToggleHold?: (r: any) => void;
}) {
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-lg p-4">
      <div className="font-medium mb-2">{title}</div>
      <div className="space-y-1 mb-3">
        {reasons.map(r => (
          <div key={r.id} className="flex items-center gap-2 text-sm">
            <span className={`flex-1 ${r.active ? '' : 'text-gray-600 line-through'}`}>{r.label}</span>
            {onToggleHold && (
              <button onClick={() => onToggleHold(r)}
                title={r.kept_hold ? 'A kept piece that comes back is held off sale — click to restock it instead' : 'A kept piece that comes back is restocked — click to hold it off sale instead'}
                className={`text-[10px] px-1.5 py-0.5 rounded ${r.kept_hold ? 'bg-teal-900 text-teal-100' : 'text-gray-600 hover:text-gray-300'}`}>
                hold
              </button>
            )}
            <button onClick={() => onToggle(r)} className="text-xs text-gray-500 hover:text-white">{r.active ? 'disable' : 'enable'}</button>
            <button onClick={() => onDelete(r.id)} className="text-xs text-gray-600 hover:text-red-400">✕</button>
          </div>
        ))}
        {reasons.length === 0 && <div className="text-gray-600 text-sm">No reasons yet.</div>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={e => onDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onAdd(); }} placeholder={`New ${kind} reason…`}
          className="flex-1 px-3 py-1.5 rounded bg-[#0f1117] border border-gray-700 text-sm focus:outline-none focus:border-blue-500" />
        <button onClick={onAdd} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-sm">Add</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Exchange composer — used inside the order detail panel. Mark which item(s) the
// customer returns for exchange, search the store catalogue for the replacement,
// pick a reason; submitting reserves the wanted piece(s) and adds the order to
// Returns → Exchange orders.
// ─────────────────────────────────────────────────────────────────────────────
export interface ExistingExchange {
  id: number; returned_items: any[]; wanted_items: any[]; reason_text?: string | null;
  send_mode?: string; no_return?: boolean; dispatched_at?: string | null;
  /** what the replacement's label collects (backend send_again.py, 27.10); null = nothing */
  cod_amount?: number | null;
}

// "Send again" (Fernando 27.10): a lost parcel or items missing from it go out as an EXCHANGE "for" the lost line —
// the new piece is tied to it, so when the original turns up it is not refunded. These reasons mean nothing comes back.
const NOTHING_COMES_BACK = ['Lost by carrier', 'Items missing from the parcel'];
interface ExMoney { customer_paid: boolean | null; paid_note: string; agreed: number; default: number; max: number; currency: string }

export function ExchangeComposer({ orderId, products, onCreated, webshopId, existing, onCancelEdit, sendAgain, onClose }:
  { orderId: string; products: any[]; onCreated: () => void; webshopId?: string;
    /** edit an open exchange instead of creating one (PATCH) */
    existing?: ExistingExchange | null; onCancelEdit?: () => void;
    /** opened from the order panel's "Send again" → Exchange: open at once, the reason preselected by its label */
    sendAgain?: { reason?: string } | null; onClose?: () => void }) {
  const editing = !!existing;
  const [openPanel, setOpenPanel] = useState(editing || !!sendAgain);
  const [returnedQty, setReturnedQty] = useState<Record<string, number>>(() => {
    const q: Record<string, number> = {};
    (existing?.returned_items || []).forEach((i: any) => { q[String(i.variation_id)] = Number(i.qty || 1); });
    return q;
  });
  // "came back as…": the garment that returns for a line when it is not the line's variant
  // (CS's word → back_variation_id; a pack-scan fact shows as read-only text)
  const csBackOf = (items: any[]) => {
    const b: Record<string, any> = {};
    (items || []).forEach((i: any) => { if (i.shipped?.source === 'cs') b[String(i.variation_id)] = i.shipped; });
    return b;
  };
  const [backOverride, setBackOverride] = useState<Record<string, any>>(() => csBackOf(existing?.returned_items || []));
  const [backFor, setBackFor] = useState<string | null>(null);
  const [backFamily, setBackFamily] = useState<any[]>([]);
  useEffect(() => {
    if (!backFor) { setBackFamily([]); return; }
    const store = webshopId ? `&webshop_id=${encodeURIComponent(webshopId)}` : '';
    authFetch(`${R}/product-family?variation_id=${encodeURIComponent(backFor)}${store}`)
      .then(r => r.ok ? r.json() : { results: [] }).then(d => setBackFamily(d.results || [])).catch(() => setBackFamily([]));
  }, [backFor, webshopId]);
  const packBack = (vid: string) => (existing?.returned_items || []).find((i: any) => String(i.variation_id) === vid && i.shipped && i.shipped.source !== 'cs')?.shipped;
  const [wanted, setWanted] = useState<any[]>(() => (existing?.wanted_items || []).map((w: any) => ({ ...w, qty: Number(w.qty || 1) })));
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [reasons, setReasons] = useState<any[]>([]);
  const [reasonId, setReasonId] = useState<string>('');
  const [reasonText, setReasonText] = useState('');
  // 'immediate' sends the replacement now; 'on_return' waits for the old item to
  // arrive at Accept Returns. noReturn = no parcel is coming back at all.
  const [sendMode, setSendMode] = useState<'immediate' | 'on_return'>((existing?.send_mode as any) === 'on_return' ? 'on_return' : 'immediate');
  const [noReturn, setNoReturn] = useState(!!existing?.no_return);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // edit mode: an untouched reason stays as it is (the backend treats null as unchanged)
  const [reasonTouched, setReasonTouched] = useState(false);

  useEffect(() => {
    if (openPanel) authFetch(`${R}/reasons?kind=exchange&active_only=true`).then(r => r.json()).then(d => {
      const list = d.reasons || [];
      setReasons(list);
      const pre = sendAgain?.reason ? list.find((r: any) => r.label === sendAgain.reason) : null;
      if (pre && !editing) { setReasonId(String(pre.id)); setReasonTouched(true); }
    });
  }, [openPanel]);  // eslint-disable-line react-hooks/exhaustive-deps
  // a lost parcel / missing items: the customer has nothing to send back (Fernando 27.10)
  useEffect(() => {
    const label = reasons.find((r: any) => String(r.id) === reasonId)?.label;
    if (!editing && label && NOTHING_COMES_BACK.includes(label)) { setNoReturn(true); setSendMode('immediate'); }
  }, [reasonId, reasons]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // the panel is re-pointed at another exchange (order panel: Edit on the pass card) — or
    // edit mode ended: then EVERYTHING is cleared, or the still-mounted composer would sit
    // pre-filled in create mode and one click would make a duplicate exchange (review H11)
    if (!existing) {
      setReturnedQty({}); setWanted([]); setReasonId(''); setReasonText(''); setReasonTouched(false);
      setSendMode('immediate'); setNoReturn(false); setOpenPanel(false); setMsg(''); setBackOverride({}); setBackFor(null);
      return;
    }
    const q: Record<string, number> = {};
    (existing.returned_items || []).forEach((i: any) => { q[String(i.variation_id)] = Number(i.qty || 1); });
    setReturnedQty(q);
    setBackOverride(csBackOf(existing.returned_items || [])); setBackFor(null);
    setWanted((existing.wanted_items || []).map((w: any) => ({ ...w, qty: Number(w.qty || 1) })));
    setSendMode(existing.send_mode === 'on_return' ? 'on_return' : 'immediate');
    setNoReturn(!!existing.no_return);
    setReasonId(''); setReasonText(existing.reason_text || ''); setReasonTouched(false);
    setOpenPanel(true); setMsg('');
  }, [existing?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(async () => {
      if (searchInput.trim().length < 2) { setResults([]); return; }
      // scoped to the ORDER's store — without it the backend defaults to Big
      // Oliver and a Nora/Motley exchange gets the wrong catalogue
      const store = webshopId ? `&webshop_id=${encodeURIComponent(webshopId)}` : '';
      const res = await authFetch(`${R}/product-search?q=${encodeURIComponent(searchInput.trim())}${store}`);
      if (res.ok) setResults((await res.json()).results || []);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, webshopId]);

  const setRet = (vid: string, q: number, max: number) => setReturnedQty(s => ({ ...s, [vid]: Math.max(0, Math.min(max, q)) }));
  const addWanted = (r: any) => { if (!wanted.find(w => w.variation_id === r.variation_id)) setWanted(w => [...w, { ...r, qty: 1 }]); setSearchInput(''); setResults([]); };
  const setWantQty = (vid: string, q: number) => setWanted(w => w.map(x => x.variation_id === vid ? { ...x, qty: Math.max(1, q) } : x));
  const removeWanted = (vid: string) => setWanted(w => w.filter(x => x.variation_id !== vid));

  const returnedItems = Object.entries(returnedQty).filter(([, q]) => q > 0)
    .map(([variation_id, qty]) => ({ variation_id, qty, back_variation_id: backOverride[variation_id]?.variation_id || null }));

  // What the replacement's label collects (send_again.py): nothing when the customer has paid; when they have NOT paid
  // (a lost cash-on-delivery parcel) what they agreed to pay for these lines — lower is allowed, higher never.
  const [money, setMoney] = useState<ExMoney | null>(null);
  const [codAmount, setCodAmount] = useState<string>(existing?.cod_amount != null ? String(existing.cod_amount) : '');
  const [codTouched, setCodTouched] = useState(existing?.cod_amount != null);
  const retKey = JSON.stringify(returnedItems.map(i => [i.variation_id, i.qty]));
  useEffect(() => {
    if (!openPanel || returnedItems.length === 0) { setMoney(null); return; }
    let live = true;
    authFetch(`${R}/exchange-money`, { method: 'POST',
      body: JSON.stringify({ order_number: orderId, returned_items: returnedItems.map(i => ({ variation_id: i.variation_id, qty: i.qty })) }) })
      .then(r => r.ok ? r.json() : null)
      .then((d: ExMoney | null) => {
        if (!live) return;
        setMoney(d);
        if (d && !codTouched) setCodAmount(String(d.default ?? 0));
      }).catch(() => { if (live) setMoney(null); });
    return () => { live = false; };
  }, [openPanel, retKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  const codValue = money ? Number(codAmount || 0) : undefined;
  const codTooHigh = !!money && codValue !== undefined && codValue > money.max + 0.005;

  const submit = async () => {
    if (returnedItems.length === 0 || wanted.length === 0) return;
    setBusy(true);
    if (editing && existing) {
      // PATCH: stock follows the diff of the wanted items; the pass follows the send mode
      const body: any = {
        returned_items: returnedItems,
        wanted_items: wanted.map(w => ({ variation_id: w.variation_id, qty: w.qty })),
        send_mode: noReturn ? 'immediate' : sendMode,
        no_return: noReturn,
      };
      if (money && codTouched && !existing.dispatched_at) body.cod_amount = codValue;
      if (reasonTouched) { body.reason_id = reasonId ? Number(reasonId) : null; body.reason_text = reasonId ? null : (reasonText.trim() || null); }
      const res = await authFetch(`${R}/exchange-requests/${existing.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      setBusy(false);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Could not change the exchange')); return; }
      const bad = [...(d.reserve_results || []), ...(d.release_results || [])].filter((x: any) => !x.ok);
      setMsg(bad.length
        ? `⚠ Changed, but a stock write failed: ${bad.map((x: any) => `${x.variation_id} — ${x.reason || '?'}`).join('; ')}. Check the Exchanges tab.`
        : `✓ Exchange E${existing.id} changed${d.changes?.length ? ': ' + d.changes.join('; ') : ''}${d.pass ? ` — ${d.pass}` : ''}.`);
      onCreated();
      return;
    }
    const res = await authFetch(`${R}/exchange-requests`, {
      method: 'POST',
      body: JSON.stringify({
        // The field is named order_number but the backend resolves it through
        // _find_order, which matches the WMS id first — passing the id here is
        // deliberate and exact (order numbers could in theory collide; ids can't).
        order_number: orderId, returned_items: returnedItems,
        wanted_items: wanted.map(w => ({ variation_id: w.variation_id, qty: w.qty })),
        reason_id: reasonId ? Number(reasonId) : null,
        reason_text: reasonId ? null : (reasonText.trim() || null),
        send_mode: noReturn ? 'immediate' : sendMode,
        no_return: noReturn,
        ...(money ? { cod_amount: codValue } : {}),
      }),
    });
    setBusy(false);
    if (!res.ok) { const e = await res.json().catch(() => ({})); setMsg(typeof e.detail === 'string' ? e.detail : (e.detail?.message || e.detail?.[0]?.msg || 'Could not create exchange')); return; }
    const d = await res.json();
    if (!d.reserved) {
      // Partial reservation: the row is open and holds what it could. Say which
      // items failed and why, and leave the panel open — a two-second toast is
      // how NM1405 got marked three times.
      const bad = (d.reserve_results || []).filter((r: any) => !r.ok);
      setMsg(`⚠ Exchange created, but not everything could be reserved${bad.length
        ? ': ' + bad.map((r: any) => `${r.article_number} — ${r.reason || 'no route'}`).join('; ')
        : ''}. The order stays put; resolve or Release it from the Exchanges tab.`);
      onCreated();
      return;
    }
    if (!d.pass_started && (noReturn || sendMode === 'immediate')) {
      setMsg(`⚠ Stock reserved, but the order could not start its exchange pass${d.pass_reason ? `: ${d.pass_reason}` : ''}.`);
      onCreated();
      return;
    }
    setMsg(`✓ Exchange created and stock reserved${d.cod_amount ? ` — the label collects ${Number(d.cod_amount).toFixed(2)} ${money?.currency || ''}` : ''}.`);
    setReturnedQty({}); setWanted([]); setReasonId(''); setReasonText('');
    setSendMode('immediate'); setNoReturn(false);
    onCreated();
    // Send again: the dialog closes itself once the exchange is in; the Exchange orders tab folds the panel
    setTimeout(() => { if (sendAgain) { onClose?.(); } else { setOpenPanel(false); setMsg(''); } }, 1800);
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2.5">
        <span className="text-white text-sm font-semibold">{editing ? `Edit exchange E${existing!.id}` : sendAgain ? 'Send again — exchange' : 'Exchange'}</span>
        <button onClick={() => { if (editing) { onCancelEdit?.(); } else if (sendAgain) { onClose?.(); } else setOpenPanel(o => !o); }}
          className="text-xs px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white">
          {editing ? 'Close without saving' : sendAgain ? 'Close' : openPanel ? 'Cancel' : 'Mark exchange'}
        </button>
      </div>
      {editing && existing?.dispatched_at && (
        <div className="px-4 pt-3 text-xs text-amber-300">The replacement has been dispatched — only the reason can still change.</div>
      )}
      {openPanel && (
        <div className="p-4 space-y-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">{sendAgain
              ? '1 · Item(s) lost or missing — what goes out replaces these lines'
              : '1 · Item(s) the customer returns'}</div>
            <div className="space-y-1">
              {products.map((p: any) => {
                const vid = String(p.variation_id);
                const ov = backOverride[vid];
                const pb = packBack(vid);
                return (
                  <div key={vid}>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 text-gray-300">
                        {(ov || pb) ? (
                          <>
                            <span className="text-[10px] uppercase tracking-wider text-red-300 mr-1">expect back</span>
                            {(ov || pb).article_number || titleEn(ov || pb)}
                            <span className="text-gray-500 text-xs ml-2">· sold as {p.product_code || titleEn(p)}{p.model_name ? ` ${p.model_name}` : ''}</span>
                          </>
                        ) : (
                          <>{titleEn(p)}{p.model_name && <span className="text-gray-500 ml-1">{p.model_name}</span>}</>
                        )}
                      </span>
                      <button onClick={() => setRet(vid, (returnedQty[vid] || 0) - 1, p.quantity)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">−</button>
                      <span className="w-6 text-center">{returnedQty[vid] || 0}</span>
                      <button onClick={() => setRet(vid, (returnedQty[vid] || 0) + 1, p.quantity)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">+</button>
                      <span className="text-gray-600 text-xs w-10">/ {p.quantity}</span>
                      {(returnedQty[vid] || 0) > 0 && !wanted.find(w => String(w.variation_id) === vid) && !existing?.dispatched_at && (
                        <button onClick={() => setWanted(w => [...w, { variation_id: vid, product_name: p.product_name, product_name_en: p.product_name_en,
                                                                     size: p.model_name || '', qty: returnedQty[vid] || 1 }])}
                          title="Send the identical item again — it is still tied to this line"
                          className="text-[11px] px-2 py-0.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800">＝ same item again</button>
                      )}
                    </div>
                    {!existing?.dispatched_at && (
                      <div className="text-[10px] text-gray-500 flex gap-2 items-center flex-wrap">
                        {ov ? (
                          <span>CS: the customer has this garment, not the line ordered <button onClick={() => setBackOverride(o => { const n = { ...o }; delete n[vid]; return n; })} className="underline hover:text-white">undo</button></span>
                        ) : pb ? (
                          <span>🚚 left this way at packing — the desk expects it by its own barcode</span>
                        ) : null}
                        {!ov && (
                          <button onClick={() => setBackFor(backFor === vid ? null : vid)} className="underline decoration-dotted hover:text-white">
                            {backFor === vid ? 'close' : 'came back as a different size…'}
                          </button>
                        )}
                        {backFor === vid && backFamily.filter(f => String(f.variation_id) !== vid).map(f => (
                          <button key={f.variation_id} onClick={() => { setBackOverride(o => ({ ...o, [vid]: { variation_id: f.variation_id, article_number: f.product_code, product_name: f.product_name } })); setBackFor(null); }}
                            className="px-1.5 py-0.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800">{f.size || f.product_code}</button>
                        ))}
                        {backFor === vid && backFamily.length === 0 && <span className="text-gray-600">no other sizes found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">{sendAgain
              ? '2 · What goes out instead (another size / colour — or "same item again" above)'
              : '2 · Replacement the customer wants'}</div>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search product code, barcode or name…"
              className="w-full px-3 py-2 rounded bg-[#0f1117] border border-gray-700 focus:outline-none focus:border-blue-500" />
            {results.length > 0 && (
              <div className="mt-1 border border-gray-800 rounded max-h-48 overflow-auto">
                {results.map(r => (
                  <div key={r.variation_id} onClick={() => addWanted(r)} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-800 cursor-pointer">
                    <span className="flex-1">{titleEn(r)} <span className="text-gray-500">{r.size || ''}</span></span>
                    <span className="text-gray-500 text-xs">{r.picklist}</span>
                    <span className={`text-xs ${r.stock_qty > 0 ? 'text-green-400' : 'text-amber-500'}`}>stock {r.stock_qty}</span>
                  </div>
                ))}
              </div>
            )}
            {wanted.length > 0 && (
              <div className="mt-2 space-y-1">
                {wanted.map(w => (
                  <div key={w.variation_id} className="flex items-center gap-2 bg-[#0f1117] border border-gray-800 rounded px-3 py-1.5">
                    {/* the flag is KNOWN only for items added from the product search; an item
                        prefilled from a stored exchange (edit mode) has no flag and gets no label —
                        "unknown" used to print as "MCF stock" (Fernando, DK14663, 2026-09-17) */}
                    <span className="flex-1">{titleEn(w)} <span className="text-gray-500">{w.size || ''}</span>{w.wgr_matched === false && <span className="text-amber-500 text-xs ml-1">· not in WGR</span>}</span>
                    <button onClick={() => setWantQty(w.variation_id, w.qty - 1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">−</button>
                    <span className="w-6 text-center">{w.qty}</span>
                    <button onClick={() => setWantQty(w.variation_id, w.qty + 1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">+</button>
                    <button onClick={() => removeWanted(w.variation_id)} className="text-gray-600 hover:text-red-400">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">3 · When does the replacement go?</div>
            <div className="space-y-1.5">
              <label className={`flex items-center gap-2 ${noReturn ? 'opacity-50' : 'cursor-pointer'}`}>
                <input type="radio" name="ex-send-mode" checked={noReturn || sendMode === 'immediate'}
                  disabled={noReturn} onChange={() => setSendMode('immediate')} />
                <span className="text-gray-300">Send the replacement now</span>
                <span className="text-gray-600 text-xs">— it ships today; the old item follows</span>
              </label>
              <label className={`flex items-center gap-2 ${noReturn ? 'opacity-50' : 'cursor-pointer'}`}>
                <input type="radio" name="ex-send-mode" checked={!noReturn && sendMode === 'on_return'}
                  disabled={noReturn} onChange={() => setSendMode('on_return')} />
                <span className="text-gray-300">Send when the return arrives</span>
                <span className="text-gray-600 text-xs">— the piece is reserved and ships once the old item is accepted</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer pt-1 border-t border-gray-800">
                <input type="checkbox" checked={noReturn}
                  onChange={e => { setNoReturn(e.target.checked); if (e.target.checked) setSendMode('immediate'); }} />
                <span className="text-gray-300">The customer keeps the original — nothing comes back</span>
                <span className="text-gray-600 text-xs">— the replacement sends now; if the original comes back anyway it is not refunded (held off sale when the reason says hold)</span>
              </label>
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">4 · Reason</div>
            <div className="flex gap-2">
              <select value={reasonId} onChange={e => { setReasonId(e.target.value); setReasonTouched(true); }} className="px-3 py-2 rounded bg-[#0f1117] border border-gray-700 focus:outline-none focus:border-blue-500">
                <option value="">— pick a reason —</option>
                {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              {!reasonId && (
                <input value={reasonText} onChange={e => { setReasonText(e.target.value); setReasonTouched(true); }} placeholder="…or type a reason"
                  className="flex-1 px-3 py-2 rounded bg-[#0f1117] border border-gray-700 focus:outline-none focus:border-blue-500" />
              )}
            </div>
            <p className="text-gray-600 text-xs mt-1">Manage the reason list in Returns → Settings.</p>
          </div>

          {money && (
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">5 · Money — what the label collects</div>
              {money.customer_paid === true ? (
                <div className="text-gray-300">Nothing to collect — {money.paid_note}.</div>
              ) : (
                <div className="space-y-1.5">
                  <div className={money.customer_paid === false ? 'text-amber-200' : 'text-gray-300'}>
                    {money.customer_paid === false ? 'The customer has not paid' : 'Payment not known'} — {money.paid_note}.
                  </div>
                  <label className="flex items-center gap-2 flex-wrap text-gray-300">
                    The label collects
                    <input type="number" min={0} step="0.01" value={codAmount} disabled={!!existing?.dispatched_at}
                      onChange={e => { setCodAmount(e.target.value); setCodTouched(true); }}
                      className={`keep-width w-28 px-2 py-1 rounded bg-[#0f1117] border text-right ${codTooHigh ? 'border-red-600' : 'border-gray-700'}`} />
                    <span>{money.currency}</span>
                    <span className="text-gray-500 text-xs">at most {money.max.toFixed(2)} — what they agreed to pay for these lines.
                      Lower it when less goes out (charge only for what is sent); never more. The shop order is changed afterwards to match.</span>
                  </label>
                  {codTooHigh && <div className="text-red-300 text-xs">More than the customer agreed to pay — the WMS never charges extra.</div>}
                </div>
              )}
            </div>
          )}

          {msg && <div className="text-sm text-gray-200">{msg}</div>}
          <div className="flex justify-end">
            <button onClick={submit} disabled={busy || returnedItems.length === 0 || wanted.length === 0 || codTooHigh}
              className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 font-medium text-white">
              {editing ? 'Save changes' : 'Create exchange & reserve'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Units per EUR, one row per currency; saved as the JSON the backend validates (2026-09-29).
function EurRatesEditor({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const parse = (v: string): Record<string, number> => { try { const d = JSON.parse(v || '{}'); return typeof d === 'object' && d ? d : {}; } catch { return {}; } };
  const [rates, setRates] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(parse(value)).map(([k, v]) => [k, String(v)])));
  const [newCur, setNewCur] = useState('');
  useEffect(() => { setRates(Object.fromEntries(Object.entries(parse(value)).map(([k, v]) => [k, String(v)]))); }, [value]);
  const save = () => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(rates)) { const n = Number(v); if (k.length === 3 && n > 0) out[k.toUpperCase()] = n; }
    onSave(JSON.stringify(out));
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {Object.entries(rates).sort().map(([k, v]) => (
          <label key={k} className="flex items-center gap-1 text-sm text-gray-300">
            <span className="font-mono w-10">{k}</span>
            <input value={v} onChange={e => setRates(r => ({ ...r, [k]: e.target.value }))}
              className="keep-width w-24 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-right text-sm" />
            <button onClick={() => setRates(r => { const n = { ...r }; delete n[k]; return n; })} className="text-gray-600 hover:text-red-300 text-xs">✕</button>
          </label>
        ))}
        <span className="flex items-center gap-1 text-sm">
          <input value={newCur} onChange={e => setNewCur(e.target.value.toUpperCase().slice(0, 3))} placeholder="ISO" className="keep-width w-14 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono" />
          <button onClick={() => { if (newCur.length === 3) { setRates(r => ({ ...r, [newCur]: '' })); setNewCur(''); } }} className="text-xs text-gray-400 hover:text-white">+ add</button>
        </span>
      </div>
      <button onClick={save} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white">Save rates</button>
    </div>
  );
}
